import {
  DOCUMENT_SESSION_PROTOCOL_VERSION,
  SPREADSHEET_ENGINE_SHEET_CHUNK_COLS,
  SPREADSHEET_ENGINE_SHEET_CHUNK_ROWS,
  buildSheetChunkId,
  buildSheetChunkKey,
  type DocumentSessionDocScope,
} from './documentSessionProtocol'

export const SPREADSHEET_ENGINE_PROTOCOL_VERSION = 1 as const

export {
  SPREADSHEET_ENGINE_SHEET_CHUNK_COLS,
  SPREADSHEET_ENGINE_SHEET_CHUNK_ROWS,
  buildSheetChunkId,
  buildSheetChunkKey,
}

export type SpreadsheetCellValueKind =
  | 'blank'
  | 'number'
  | 'text'
  | 'boolean'
  | 'datetime'
  | 'error'
  | 'pending_external'
  | 'dynamic_array'

export const SPREADSHEET_ENGINE_ERROR_CODES = [
  '#DIV/0!',
  '#VALUE!',
  '#REF!',
  '#NAME?',
  '#NUM!',
  '#N/A',
  '#SPILL!',
  '#CIRCULAR!',
] as const

export type SpreadsheetEngineErrorCode = typeof SPREADSHEET_ENGINE_ERROR_CODES[number]

export type SpreadsheetCellValue =
  | { kind: 'blank' }
  | { kind: 'number'; value: number }
  | { kind: 'text'; value: string }
  | { kind: 'boolean'; value: boolean }
  | { kind: 'datetime'; epoch_ms: number }
  | { kind: 'error'; code: SpreadsheetEngineErrorCode; message?: string }
  | { kind: 'pending_external'; request_id: string; source_id: string }
  | { kind: 'dynamic_array'; rows: SpreadsheetCellValue[][] }

export type SpreadsheetCellAddress = {
  sheet_id: string
  row: number
  col: number
}

export type SpreadsheetRangeAddress = {
  sheet_id: string
  start_row: number
  start_col: number
  end_row: number
  end_col: number
}

export type SpreadsheetFormulaCellReference = {
  sheet_name?: string | null
  row: number
  col: number
  row_absolute: boolean
  col_absolute: boolean
}

export type SpreadsheetFormulaRangeReference = {
  start: SpreadsheetFormulaCellReference
  end: SpreadsheetFormulaCellReference
}

export type SpreadsheetFormulaUnaryOperator = 'plus' | 'minus' | 'percent'

export type SpreadsheetFormulaBinaryOperator =
  | 'add'
  | 'subtract'
  | 'multiply'
  | 'divide'
  | 'power'
  | 'concat'
  | 'equal'
  | 'not_equal'
  | 'less_than'
  | 'less_than_or_equal'
  | 'greater_than'
  | 'greater_than_or_equal'

export type SpreadsheetFormulaAst =
  | { kind: 'number'; value: number }
  | { kind: 'text'; value: string }
  | { kind: 'boolean'; value: boolean }
  | { kind: 'reference_error' }
  | { kind: 'cell_reference'; reference: SpreadsheetFormulaCellReference }
  | { kind: 'spill_reference'; reference: SpreadsheetFormulaCellReference }
  | { kind: 'range_reference'; reference: SpreadsheetFormulaRangeReference }
  | { kind: 'structured_reference'; reference: SpreadsheetFormulaStructuredReference }
  | { kind: 'named_reference'; name: string }
  | { kind: 'function_call'; name: string; args: SpreadsheetFormulaAst[] }
  | { kind: 'unary_op'; op: SpreadsheetFormulaUnaryOperator; expr: SpreadsheetFormulaAst }
  | { kind: 'binary_op'; op: SpreadsheetFormulaBinaryOperator; left: SpreadsheetFormulaAst; right: SpreadsheetFormulaAst }

export type SpreadsheetFormulaStructuredReference = {
  table_name: string
  specifiers?: string[]
  column_name?: string | null
}

export type SpreadsheetFormulaDependencySet = {
  cells: SpreadsheetFormulaCellReference[]
  ranges: SpreadsheetFormulaRangeReference[]
  names: string[]
  structured_references?: SpreadsheetFormulaStructuredReference[]
  volatile: boolean
}

export type SpreadsheetFormulaParseError = {
  offset: number
  message: string
}

export type SpreadsheetFormulaAuthoringReferenceKind =
  | 'cell'
  | 'range'
  | 'whole_column'
  | 'whole_row'
  | 'spill'

export type SpreadsheetFormulaAuthoringCellCoord = {
  row: number
  col: number
}

export type SpreadsheetFormulaAuthoringRange = {
  sheet_name?: string | null
  start: SpreadsheetFormulaAuthoringCellCoord
  end: SpreadsheetFormulaAuthoringCellCoord
}

export type SpreadsheetFormulaAuthoringSpan =
  | {
    kind: 'reference'
    reference_kind: SpreadsheetFormulaAuthoringReferenceKind
    text: string
    start_offset: number
    end_offset: number
    sheet_name?: string | null
    range: SpreadsheetFormulaAuthoringRange
  }
  | {
    kind: 'function'
    name: string
    start_offset: number
    end_offset: number
    open_paren_offset: number
  }
  | {
    kind: 'named_reference'
    name: string
    start_offset: number
    end_offset: number
  }

export type SpreadsheetFormulaAuthoringAnalysis = {
  spans: SpreadsheetFormulaAuthoringSpan[]
  parse_error?: SpreadsheetFormulaParseError | null
}

export type SpreadsheetCellRecord = {
  raw_input: string
  parsed_formula?: SpreadsheetFormulaAst | null
  computed_value?: SpreadsheetCellValue | null
  style_id?: string | null
  validation_id?: string | null
  comment?: string | null
  hyperlink?: string | null
  protection?: Record<string, unknown> | null
  revision: number
  metadata?: {
    formula_dependencies?: SpreadsheetFormulaDependencySet
    formula_parse_error?: SpreadsheetFormulaParseError
    [key: string]: unknown
  } | null
}

export type SpreadsheetSheetProtectionOptions = {
  enabled?: boolean
  allow_select_locked_cells?: boolean
  allow_select_unlocked_cells?: boolean
  allow_format_cells?: boolean
  allow_resize_rows?: boolean
  allow_resize_columns?: boolean
  allow_insert_rows?: boolean
  allow_insert_columns?: boolean
  allow_delete_rows?: boolean
  allow_delete_columns?: boolean
  [key: string]: unknown
}

export type SpreadsheetSheetProtectedRange = {
  name?: string | null
  range: string
  security_descriptor?: string | null
  password_hash?: string | null
  algorithm?: string | null
  hash_value?: string | null
  salt_value?: string | null
  spin_count?: number | string | null
}

export type SpreadsheetSheetSnapshot = {
  sheet_id: string
  name: string
  row_count: number
  col_count: number
  chunks: Record<string, Record<string, SpreadsheetCellRecord>>
  row_metadata?: Record<string, Record<string, unknown>>
  column_metadata?: Record<string, Record<string, unknown>>
  ooxml_sheet_metadata?: SpreadsheetSheetOoxmlMetadata | null
  tab_color?: Record<string, unknown> | null
  sheet_format_properties?: SpreadsheetSheetFormatProperties | null
  calculation_properties?: SpreadsheetSheetCalculationProperties | null
  sheet_views?: SpreadsheetSheetView[] | null
  custom_sheet_views?: SpreadsheetSheetCustomView[] | null
  print_settings?: SpreadsheetSheetPrintSettings | null
  ignored_errors?: SpreadsheetSheetIgnoredError[] | null
  frozen_panes?: { rows: number; cols: number } | null
  hidden?: boolean
  sheet_protection?: SpreadsheetSheetProtectionOptions | null
  protected_ranges?: SpreadsheetSheetProtectedRange[] | null
  auto_filter?: SpreadsheetAutoFilter | null
  tables?: Record<string, SpreadsheetTableDefinition>
  merge_ranges?: Record<string, SpreadsheetRangeAddress>
}

export type SpreadsheetSheetOoxmlRawElement = {
  raw_xml: string
  attributes?: Record<string, string> | null
}

export type SpreadsheetSheetOoxmlExtension = {
  uri?: string | null
  raw_xml: string
}

export type SpreadsheetSheetOoxmlExtensionList = {
  raw_xml: string
  extensions?: SpreadsheetSheetOoxmlExtension[] | null
}

export type SpreadsheetSheetOoxmlMetadata = {
  phonetic_properties?: SpreadsheetSheetOoxmlRawElement | null
  smart_tags?: SpreadsheetSheetOoxmlRawElement | null
  scenarios?: SpreadsheetSheetOoxmlRawElement | null
  extension_list?: SpreadsheetSheetOoxmlExtensionList | null
}

export type SpreadsheetSheetView = {
  workbook_view_id?: number | null
  show_grid_lines?: boolean
  show_row_col_headers?: boolean
  show_formulas?: boolean
  right_to_left?: boolean
  tab_selected?: boolean
  zoom_scale?: number | null
  zoom_scale_normal?: number | null
  top_left_cell?: string | null
  active_cell?: string | null
  selection_sqref?: string | null
}

export type SpreadsheetSheetCustomView = {
  guid?: string | null
  name?: string | null
  scale?: number | null
  color_id?: number | null
  show_page_breaks?: boolean
  show_formulas?: boolean
  show_grid_lines?: boolean
  show_row_col_headers?: boolean
  outline_symbols?: boolean
  zero_values?: boolean
  fit_to_page?: boolean
  print_area?: boolean
  filter?: boolean
  hidden_rows?: boolean
  hidden_columns?: boolean
  state?: string | null
  top_left_cell?: string | null
  active_cell?: string | null
  selection_sqref?: string | null
}

export type SpreadsheetSheetFormatProperties = {
  default_row_height_px?: number | null
  default_col_width_px?: number | null
  base_col_width?: number | null
  zero_height?: boolean
  thick_top?: boolean
  thick_bottom?: boolean
  outline_level_row?: number | null
  outline_level_col?: number | null
  outline_summary_below?: boolean
  outline_summary_right?: boolean
  show_outline_symbols?: boolean
  apply_outline_styles?: boolean
}

export type SpreadsheetSheetCalculationProperties = {
  full_calc_on_load?: boolean
}

export type SpreadsheetSheetPrintSettings = {
  print_options?: {
    horizontal_centered?: boolean
    vertical_centered?: boolean
    headings?: boolean
    grid_lines?: boolean
    grid_lines_set?: boolean
  } | null
  page_margins?: {
    left?: number | null
    right?: number | null
    top?: number | null
    bottom?: number | null
    header?: number | null
    footer?: number | null
  } | null
  page_setup?: {
    /** OOXML sheetPr/pageSetUpPr: selects fit dimensions instead of percentage scaling. */
    fit_to_page?: boolean
    paper_size?: number | null
    scale?: number | null
    fit_to_width?: number | null
    fit_to_height?: number | null
    orientation?: 'portrait' | 'landscape' | null
    page_order?: 'downThenOver' | 'overThenDown' | null
    horizontal_dpi?: number | null
    vertical_dpi?: number | null
    first_page_number?: number | null
    use_first_page_number?: boolean
    black_and_white?: boolean
    draft?: boolean
    cell_comments?: string | null
    errors?: string | null
  } | null
  header_footer?: {
    different_odd_even?: boolean
    different_first?: boolean
    scale_with_doc?: boolean
    align_with_margins?: boolean
    odd_header?: string | null
    odd_footer?: string | null
    even_header?: string | null
    even_footer?: string | null
    first_header?: string | null
    first_footer?: string | null
  } | null
  page_breaks?: {
    rows?: SpreadsheetSheetPageBreak[] | null
    columns?: SpreadsheetSheetPageBreak[] | null
  } | null
}

export type SpreadsheetSheetPageBreak = {
  id: number
  min?: number | null
  max?: number | null
  manual?: boolean
}

export type SpreadsheetSheetIgnoredError = {
  range: string
  eval_error?: boolean
  two_digit_text_year?: boolean
  number_stored_as_text?: boolean
  formula?: boolean
  formula_range?: boolean
  unlocked_formula?: boolean
  empty_cell_reference?: boolean
  list_data_validation?: boolean
  calculated_column?: boolean
}

export type SpreadsheetWorkbookSnapshot = {
  protocol_version: typeof SPREADSHEET_ENGINE_PROTOCOL_VERSION
  workbook_id: string
  workbook_revision: number
  workbook_properties?: SpreadsheetWorkbookProperties | null
  workbook_security?: SpreadsheetWorkbookSecurity | null
  document_properties?: SpreadsheetWorkbookDocumentProperties | null
  calculation_settings?: SpreadsheetWorkbookCalculationSettings | null
  workbook_views?: SpreadsheetWorkbookView[] | null
  workbook_theme?: SpreadsheetWorkbookTheme | null
  custom_properties?: Record<string, SpreadsheetWorkbookCustomProperty>
  sheets: SpreadsheetSheetSnapshot[]
  sheet_order: string[]
  named_ranges: Record<string, SpreadsheetRangeAddress>
  defined_names?: Record<string, SpreadsheetDefinedNameEntry>
  validation_rules: Record<string, SpreadsheetValidationRule>
  styles: Record<string, Record<string, unknown>>
  table_styles?: Record<string, SpreadsheetTableStyleDefinition>
  conditional_formats?: Record<string, Record<string, unknown>>
  sparkline_groups?: Record<string, SpreadsheetSparklineGroup>
  derived_artifacts?: Record<string, SpreadsheetDerivedArtifact>
  external_sources: Record<string, Record<string, unknown>>
  ooxml_attachments?: SpreadsheetExcelOoxmlArtifactManifestEntry[]
}

export type SpreadsheetWorkbookProperties = {
  date_1904?: boolean
  code_name?: string | null
}

export type SpreadsheetWorkbookSecurity = {
  workbook_protection?: {
    workbook_password?: string | null
    revisions_password?: string | null
    lock_structure?: boolean
    lock_windows?: boolean
    lock_revision?: boolean
    algorithm_name?: string | null
    hash_value?: string | null
    salt_value?: string | null
    spin_count?: number | null
  } | null
  file_sharing?: {
    read_only_recommended?: boolean
    user_name?: string | null
    reservation_password?: string | null
    algorithm_name?: string | null
    hash_value?: string | null
    salt_value?: string | null
    spin_count?: number | null
  } | null
}

export type SpreadsheetWorkbookDocumentProperties = {
  title?: string | null
  subject?: string | null
  creator?: string | null
  keywords?: string | null
  description?: string | null
  last_modified_by?: string | null
  revision?: string | null
  created?: string | null
  modified?: string | null
  category?: string | null
  content_status?: string | null
  application?: string | null
  app_version?: string | null
  company?: string | null
  manager?: string | null
}

export type SpreadsheetWorkbookCalculationSettings = {
  calc_mode?: 'auto' | 'manual' | 'autoNoTable' | null
  full_calc_on_load?: boolean
  force_full_calc?: boolean
  iterate?: boolean
  iterate_count?: number | null
  iterate_delta?: number | null
  calc_id?: number | null
}

export type SpreadsheetWorkbookView = {
  active_tab?: number | null
  first_sheet?: number | null
  visibility?: 'visible' | 'hidden' | 'veryHidden' | null
  minimized?: boolean
  show_horizontal_scroll?: boolean
  show_vertical_scroll?: boolean
  show_sheet_tabs?: boolean
  tab_ratio?: number | null
  x_window?: number | null
  y_window?: number | null
  window_width?: number | null
  window_height?: number | null
}

export type SpreadsheetWorkbookThemeColor = {
  name: string
  rgb?: string | null
  system_color?: string | null
  last_color?: string | null
}

export type SpreadsheetWorkbookTheme = {
  name?: string | null
  color_scheme?: {
    name?: string | null
    colors?: Record<string, SpreadsheetWorkbookThemeColor>
  } | null
}

export type SpreadsheetWorkbookCustomProperty = {
  name: string
  kind: 'string' | 'boolean' | 'number' | 'date'
  value: string | boolean | number
}

export type SpreadsheetRangeReadMode = 'raw' | 'computed'

export type SpreadsheetRangeCell = {
  row: number
  col: number
  raw_input: string
  value: SpreadsheetCellValue
  display_value?: string | null
  style_id?: string | null
  validation_id?: string | null
  comment?: string | null
  hyperlink?: string | null
  protection?: Record<string, unknown> | null
  metadata?: Record<string, unknown> | null
  revision: number
}

export type SpreadsheetRangeSnapshot = {
  workbook_id: string
  workbook_revision: number
  sheet_id: string
  range: SpreadsheetRangeAddress
  mode: SpreadsheetRangeReadMode
  rows: SpreadsheetRangeCell[][]
}

export type SpreadsheetGetRangePayload = {
  range: SpreadsheetRangeAddress
  mode: SpreadsheetRangeReadMode
}

export type SpreadsheetRoutedWorkerResponseMetadata = {
  requestId?: string
  workbookId?: string
  sheetId?: string | null
  workbookRevision?: number | null
}

export type SpreadsheetRangeResponsePayload = SpreadsheetRoutedWorkerResponseMetadata & {
  range: SpreadsheetRangeSnapshot
}

export type SpreadsheetClipboardCell = {
  row_offset: number
  col_offset: number
  raw_input: string
  computed_value: SpreadsheetCellValue
  display_value?: string | null
  style_id?: string | null
  style?: Record<string, unknown> | null
  validation_id?: string | null
  comment?: string | null
  hyperlink?: string | null
  protection?: Record<string, unknown> | null
  metadata?: Record<string, unknown> | null
}

export type SpreadsheetClipboardPayload = {
  workbook_id: string
  sheet_id: string
  range: SpreadsheetRangeAddress
  row_count: number
  col_count: number
  cells: SpreadsheetClipboardCell[][]
  tsv: string
}

export type SpreadsheetCopyRangePayload = {
  range: SpreadsheetRangeAddress
}

export type SpreadsheetCopyRangeToPayload = {
  sheet_id?: string | null
  source_range: SpreadsheetRangeAddress
  target_start_row: number
  target_start_col: number
  clear_op_id?: string
  set_op_id?: string
  actor_client_id?: string
  created_at_ms?: number
  validation_override?: boolean
  allowQueryTabEdit?: boolean
}

export type SpreadsheetCopyRangeFormatsToPayload = {
  sheet_id?: string | null
  source_range: SpreadsheetRangeAddress
  target_range: SpreadsheetRangeAddress
  op_id?: string
  actor_client_id?: string
  created_at_ms?: number
  allowQueryTabEdit?: boolean
}

export type SpreadsheetCopyPayloadResponsePayload = SpreadsheetRoutedWorkerResponseMetadata & {
  clipboard: SpreadsheetClipboardPayload
}

export type SpreadsheetPasteSpecialMode = 'values' | 'formulas' | 'formats' | 'transpose'
export type SpreadsheetFillRangeMode =
  | 'auto'
  | 'copyCells'
  | 'fillSeries'
  | 'fillDays'
  | 'fillWeekdays'
  | 'fillMonths'
  | 'fillYears'

export type SpreadsheetPasteTsvPayload = {
  sheet_id?: string | null
  start_row: number
  start_col: number
  tsv: string
  op_id?: string
  actor_client_id?: string
  created_at_ms?: number
  validation_override?: boolean
  allowQueryTabEdit?: boolean
}

export type SpreadsheetPasteSpecialPayload = {
  sheet_id?: string | null
  start_row: number
  start_col: number
  clipboard: SpreadsheetClipboardPayload
  mode: SpreadsheetPasteSpecialMode
  op_id?: string
  actor_client_id?: string
  created_at_ms?: number
  validation_override?: boolean
  allowQueryTabEdit?: boolean
}

export type SpreadsheetFillRangePayload = {
  sheet_id?: string | null
  source_range: SpreadsheetRangeAddress
  target_range: SpreadsheetRangeAddress
  mode?: SpreadsheetFillRangeMode
  op_id?: string
  actor_client_id?: string
  created_at_ms?: number
  validation_override?: boolean
  allowQueryTabEdit?: boolean
}

export type SpreadsheetFindReplacePayload = {
  sheet_id?: string | null
  range: SpreadsheetRangeAddress
  query: string
  replacement: string
  match_case?: boolean
  whole_cell?: boolean
  validation_override?: boolean
  op_id?: string
  actor_client_id?: string
  created_at_ms?: number
  allowQueryTabEdit?: boolean
}

export type SpreadsheetRemoveDuplicatesPayload = {
  sheet_id?: string | null
  range: SpreadsheetRangeAddress
  key_columns?: number[]
  has_header?: boolean
  validation_override?: boolean
  op_id?: string
  actor_client_id?: string
  created_at_ms?: number
  allowQueryTabEdit?: boolean
}

export type SpreadsheetSortRangeColumn = {
  col: number
  descending?: boolean
}

export type SpreadsheetSortRangePayload = {
  sheet_id?: string | null
  range: SpreadsheetRangeAddress
  sort_columns?: SpreadsheetSortRangeColumn[]
  has_header?: boolean
  validation_override?: boolean
  clear_op_id?: string
  set_op_id?: string
  actor_client_id?: string
  created_at_ms?: number
  allowQueryTabEdit?: boolean
}

export type SpreadsheetMoveRangePayload = {
  sheet_id?: string | null
  source_range: SpreadsheetRangeAddress
  target_start_row: number
  target_start_col: number
  clear_op_id?: string
  set_op_id?: string
  actor_client_id?: string
  created_at_ms?: number
  validation_override?: boolean
  allowQueryTabEdit?: boolean
}

export type SpreadsheetGetViewportPayload = {
  x: number
  y: number
  width: number
  height: number
  seq: number
  forceSpreadsheet?: boolean
  gridCols?: number
  gridRows?: number
  sheetId?: number | null
  frozenRows?: number
  frozenCols?: number
}

export type SpreadsheetViewportCellData = {
  value: string
  formula?: string
  format?: Record<string, unknown>
  validation_id?: string | null
  comment?: string | null
  hyperlink?: string | null
  protection?: Record<string, unknown> | null
  metadata?: Record<string, unknown> | null
}

export type SpreadsheetViewportCell = {
  col: number
  row: number
  data: SpreadsheetViewportCellData
}

export type SpreadsheetViewportResponsePayload = SpreadsheetRoutedWorkerResponseMetadata & {
  seq?: number
  data: SpreadsheetViewportCell[]
  startCol?: number
  endCol?: number
  startRow?: number
  endRow?: number
  source?: string
  tab_id?: string
  sheet_id?: number | null
}

export type SpreadsheetApplyOpsPayload = {
  ops: SpreadsheetEngineOperationEnvelope[]
  allowQueryTabEdit?: boolean
  styles?: Record<string, Record<string, unknown>>
  returnAcceptedOperations?: boolean
}

export type SpreadsheetRecalcIncrementalPayload = {
  maxFormulaCount: number
  schedulerToken: string
}

export type SpreadsheetEvaluateDraftFormulaPayload = {
  sheet_id?: string | null
  formula: string
}

export type SpreadsheetEvaluateDraftFormulaResponsePayload = SpreadsheetRoutedWorkerResponseMetadata & {
  value: SpreadsheetCellValue
  display_value: string
}

export type SpreadsheetPresenceUpdatePayload = {
  kind: 'layout_shared_state'
  sharedStateBuffer: SharedArrayBuffer | ArrayBuffer
}

export type SpreadsheetRecalcStepMetadata = {
  evaluated_cells: SpreadsheetCellAddress[]
  remaining_formula_count: number
  completed: boolean
  scheduler_token: string
}

export const SPREADSHEET_CELL_FORMAT_KEYS = [
  'numberFormat',
  'font',
  'fill',
  'border',
  'alignment',
  'protection',
  'bold',
  'italic',
  'underline',
  'strikethrough',
  'align',
  'verticalAlign',
  'wrapText',
  'shrinkToFit',
  'indent',
  'textRotation',
  'fontSize',
  'fontFamily',
  'textColor',
  'backgroundColor',
  'mergeAnchorRowOffset',
  'mergeAnchorColOffset',
  'mergeRowSpan',
  'mergeColSpan',
] as const

export type SpreadsheetCellFormatStyle = Record<string, unknown>
export type SpreadsheetStyleSidecars = Record<string, SpreadsheetCellFormatStyle>

const copyRecord = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? { ...(value as Record<string, unknown>) }
    : null
)

const compactStyleRecord = (value: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {}
  for (const [key, entry] of Object.entries(value)) {
    if (entry === undefined || entry === null || entry === '') continue
    if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
      const nested = compactStyleRecord(entry as Record<string, unknown>)
      if (Object.keys(nested).length > 0) out[key] = nested
      continue
    }
    out[key] = entry
  }
  return out
}

const setIfPresent = (target: Record<string, unknown>, key: string, value: unknown) => {
  if (value !== undefined && value !== null && value !== '') target[key] = value
}

export const canonicalizeSpreadsheetCellFormatStyle = (format: Record<string, unknown> | null | undefined): SpreadsheetCellFormatStyle => {
  const style: SpreadsheetCellFormatStyle = {}
  const source = format && typeof format === 'object' && !Array.isArray(format) ? format : {}
  const font = copyRecord(source.font) ?? {}
  if (typeof source.bold === 'boolean') font.bold = source.bold
  if (typeof source.italic === 'boolean') font.italic = source.italic
  if (typeof source.strikethrough === 'boolean') font.strikethrough = source.strikethrough
  if (typeof source.underline === 'boolean' || typeof source.underline === 'string') font.underline = source.underline
  if (Number.isFinite(Number(source.fontSize))) font.size = Number(source.fontSize)
  if (typeof source.fontFamily === 'string' && source.fontFamily.trim()) font.family = source.fontFamily.trim()
  if (typeof source.textColor === 'string' && source.textColor.trim()) font.color = source.textColor.trim()
  const compactFont = compactStyleRecord(font)
  if (Object.keys(compactFont).length > 0) style.font = compactFont

  const fill = copyRecord(source.fill) ?? {}
  if (typeof source.backgroundColor === 'string' && source.backgroundColor.trim()) {
    fill.kind = fill.kind ?? 'solid'
    fill.foregroundColor = source.backgroundColor.trim()
    fill.backgroundColor = fill.backgroundColor ?? source.backgroundColor.trim()
  }
  const compactFill = compactStyleRecord(fill)
  if (Object.keys(compactFill).length > 0) style.fill = compactFill

  const alignment = copyRecord(source.alignment) ?? {}
  if (source.align === 'left' || source.align === 'center' || source.align === 'right' || source.align === 'justify' || source.align === 'distributed') {
    alignment.horizontal = source.align
  }
  if (source.verticalAlign === 'top' || source.verticalAlign === 'middle' || source.verticalAlign === 'bottom' || source.verticalAlign === 'justify' || source.verticalAlign === 'distributed') {
    alignment.vertical = source.verticalAlign
  }
  if (typeof source.wrapText === 'boolean') alignment.wrapText = source.wrapText
  if (typeof source.shrinkToFit === 'boolean') alignment.shrinkToFit = source.shrinkToFit
  if (Number.isFinite(Number(source.indent))) alignment.indent = Number(source.indent)
  if (Number.isFinite(Number(source.textRotation))) alignment.textRotation = Number(source.textRotation)
  const compactAlignment = compactStyleRecord(alignment)
  if (Object.keys(compactAlignment).length > 0) style.alignment = compactAlignment

  for (const key of ['numberFormat', 'border', 'protection'] as const) {
    const record = copyRecord(source[key])
    if (record) {
      const compactRecord = compactStyleRecord(record)
      if (Object.keys(compactRecord).length > 0) style[key] = compactRecord
    }
  }

  if (style.font && typeof style.font === 'object') {
    const nextFont = style.font as Record<string, unknown>
    setIfPresent(style, 'bold', typeof nextFont.bold === 'boolean' ? nextFont.bold : undefined)
    setIfPresent(style, 'italic', typeof nextFont.italic === 'boolean' ? nextFont.italic : undefined)
    setIfPresent(style, 'underline', typeof nextFont.underline === 'boolean' || typeof nextFont.underline === 'string' ? nextFont.underline : undefined)
    setIfPresent(style, 'strikethrough', typeof nextFont.strikethrough === 'boolean' ? nextFont.strikethrough : undefined)
    setIfPresent(style, 'fontSize', Number.isFinite(Number(nextFont.size)) ? Number(nextFont.size) : undefined)
    setIfPresent(style, 'fontFamily', typeof nextFont.family === 'string' ? nextFont.family.trim() : undefined)
    setIfPresent(style, 'textColor', typeof nextFont.color === 'string' ? nextFont.color.trim() : undefined)
  }
  if (style.fill && typeof style.fill === 'object') {
    const nextFill = style.fill as Record<string, unknown>
    const color = typeof nextFill.foregroundColor === 'string' ? nextFill.foregroundColor : nextFill.backgroundColor
    setIfPresent(style, 'backgroundColor', typeof color === 'string' ? color.trim() : undefined)
  }
  if (style.alignment && typeof style.alignment === 'object') {
    const nextAlignment = style.alignment as Record<string, unknown>
    setIfPresent(style, 'align', typeof nextAlignment.horizontal === 'string' ? nextAlignment.horizontal.trim() : undefined)
    setIfPresent(style, 'verticalAlign', typeof nextAlignment.vertical === 'string' ? nextAlignment.vertical.trim() : undefined)
    setIfPresent(style, 'wrapText', typeof nextAlignment.wrapText === 'boolean' ? nextAlignment.wrapText : undefined)
    setIfPresent(style, 'shrinkToFit', typeof nextAlignment.shrinkToFit === 'boolean' ? nextAlignment.shrinkToFit : undefined)
    setIfPresent(style, 'indent', Number.isFinite(Number(nextAlignment.indent)) ? Number(nextAlignment.indent) : undefined)
    setIfPresent(style, 'textRotation', Number.isFinite(Number(nextAlignment.textRotation)) ? Number(nextAlignment.textRotation) : undefined)
  }

  for (const key of ['mergeAnchorRowOffset', 'mergeAnchorColOffset', 'mergeRowSpan', 'mergeColSpan'] as const) {
    const numeric = Number(source[key])
    if (Number.isFinite(numeric)) style[key] = numeric
  }
  return style
}

const stableSpreadsheetStyleValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stableSpreadsheetStyleValue)
  if (!value || typeof value !== 'object') return value
  const out: Record<string, unknown> = {}
  for (const key of Object.keys(value as Record<string, unknown>).sort()) {
    const nested = (value as Record<string, unknown>)[key]
    if (nested !== undefined) out[key] = stableSpreadsheetStyleValue(nested)
  }
  return out
}

export const stableSpreadsheetStyleJson = (style: SpreadsheetCellFormatStyle): string => {
  const ordered: SpreadsheetCellFormatStyle = {}
  for (const key of SPREADSHEET_CELL_FORMAT_KEYS) {
    if (Object.prototype.hasOwnProperty.call(style, key)) ordered[key] = stableSpreadsheetStyleValue(style[key])
  }
  return JSON.stringify(ordered)
}

export const buildSpreadsheetStyleId = (style: SpreadsheetCellFormatStyle): string => {
  const json = stableSpreadsheetStyleJson(style)
  let hash = 2166136261
  for (let index = 0; index < json.length; index += 1) {
    hash ^= json.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return `style_${(hash >>> 0).toString(36)}`
}

export const buildSpreadsheetStyleFromFormat = (
  format: Record<string, unknown> | null | undefined,
): { styleId: string | null; style: SpreadsheetCellFormatStyle | null } => {
  const style = canonicalizeSpreadsheetCellFormatStyle(format)
  if (Object.keys(style).length === 0) return { styleId: null, style: null }
  return { styleId: buildSpreadsheetStyleId(style), style }
}

export const canonicalizeSpreadsheetValidationRule = (
  rule: Omit<SpreadsheetValidationRule, 'id'> & { id?: string | null },
): SpreadsheetValidationRule => {
  const kind = rule.kind
  const normalized: SpreadsheetValidationRule = {
    id: String(rule.id ?? '').trim(),
    kind,
    allow_blank: rule.allow_blank === true,
  }
  if (kind === 'list') {
    const formula1 = String(rule.formula1 ?? '').trim()
    const values = Array.isArray(rule.values)
      ? rule.values.map((value) => String(value ?? '').trim()).filter(Boolean)
      : []
    if (formula1) normalized.formula1 = formula1
    if (values.length > 0) normalized.values = values
  } else if (kind === 'custom') {
    normalized.formula1 = String(rule.formula1 ?? '').trim()
  } else {
    normalized.operator = rule.operator ?? 'between'
    normalized.formula1 = String(rule.formula1 ?? '').trim()
    if (normalized.operator === 'between' || normalized.operator === 'notBetween') {
      normalized.formula2 = String(rule.formula2 ?? '').trim()
    }
  }
  const inputTitle = String(rule.input_title ?? '').trim()
  const inputMessage = String(rule.input_message ?? '').trim()
  const errorTitle = String(rule.error_title ?? '').trim()
  const errorMessage = String(rule.error_message ?? '').trim()
  if (inputTitle) normalized.input_title = inputTitle
  if (inputMessage) normalized.input_message = inputMessage
  if (rule.error_style === 'warning' || rule.error_style === 'information') {
    normalized.error_style = rule.error_style
  } else {
    normalized.error_style = 'stop'
  }
  if (errorTitle) normalized.error_title = errorTitle
  if (errorMessage) normalized.error_message = errorMessage
  if (!normalized.id) normalized.id = buildSpreadsheetValidationRuleId(normalized)
  return normalized
}

export const stableSpreadsheetValidationRuleJson = (
  rule: SpreadsheetValidationRule,
): string => JSON.stringify({
  kind: rule.kind,
  operator: rule.operator ?? null,
  allow_blank: rule.allow_blank === true,
  values: rule.values ?? null,
  formula1: rule.formula1 ?? null,
  formula2: rule.formula2 ?? null,
  input_title: rule.input_title ?? null,
  input_message: rule.input_message ?? null,
  error_style: rule.error_style && rule.error_style !== 'stop' ? rule.error_style : null,
  error_title: rule.error_title ?? null,
  error_message: rule.error_message ?? null,
})

export const buildSpreadsheetValidationRuleId = (
  rule: Omit<SpreadsheetValidationRule, 'id'>,
): string => {
  const json = stableSpreadsheetValidationRuleJson({ ...rule, id: '' })
  let hash = 2166136261
  for (let index = 0; index < json.length; index += 1) {
    hash ^= json.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return `validation_${(hash >>> 0).toString(36)}`
}

export type SpreadsheetDeltaResponsePayload = SpreadsheetRoutedWorkerResponseMetadata & {
  delta: SpreadsheetEngineDelta
  recalc_step?: SpreadsheetRecalcStepMetadata
  accepted_operations?: SpreadsheetEngineOperationEnvelope[]
}

export const SPREADSHEET_ENGINE_OPERATION_TYPES = [
  'SetCellInput',
  'SetRangeInput',
  'ClearRange',
  'SetFormat',
  'ClearFormats',
  'SetRowFormat',
  'SetColumnFormat',
  'MergeCells',
  'MergeAcross',
  'UnmergeCells',
  'UpsertConditionalFormatRule',
  'DeleteConditionalFormatRule',
  'ReorderConditionalFormatRules',
  'ClearConditionalFormats',
  'UpsertSparklineGroup',
  'DeleteSparklineGroup',
  'ClearSparklines',
  'GroupSparklineLocations',
  'UngroupSparklineGroup',
  'UpsertDerivedArtifact',
  'DeleteDerivedArtifact',
  'InsertRows',
  'DeleteRows',
  'InsertColumns',
  'DeleteColumns',
  'ResizeRows',
  'ResizeColumns',
  'RenameSheet',
  'AddSheet',
  'DuplicateSheet',
  'DeleteSheet',
  'MoveSheet',
  'SetSheetHidden',
  'SetSheetTabColor',
  'SetSheetProtection',
  'UpsertValidationRule',
  'DeleteValidationRule',
  'SetValidation',
  'SetComment',
  'SetHyperlink',
  'SetProtection',
  'DefineName',
  'DeleteName',
  'SetFrozenPanes',
  'SetHiddenRows',
  'SetHiddenColumns',
  'SetAutoFilter',
  'UpsertTable',
  'DeleteTable',
  'MaterializeDerivedRange',
] as const

export type SpreadsheetEngineOperationType = typeof SPREADSHEET_ENGINE_OPERATION_TYPES[number]

export type SpreadsheetEngineOperationEnvelope<TPayload = unknown> = {
  op_id: string
  op_type: SpreadsheetEngineOperationType
  workbook_id: string
  sheet_id?: string | null
  base_revision: number
  actor_client_id: string
  created_at_ms: number
  payload: TPayload
}

export type SpreadsheetStructuralOperationType = Extract<
  SpreadsheetEngineOperationType,
  'InsertRows' | 'DeleteRows' | 'InsertColumns' | 'DeleteColumns' | 'ResizeRows' | 'ResizeColumns'
>

export type SpreadsheetRowStructuralPayload = {
  sheet_id: string
  row_index: number
  count: number
}

export type SpreadsheetColumnStructuralPayload = {
  sheet_id: string
  col_index: number
  count: number
}

export type SpreadsheetResizeRowsPayload = SpreadsheetRowStructuralPayload & {
  height_px: number
}

export type SpreadsheetResizeColumnsPayload = SpreadsheetColumnStructuralPayload & {
  width_px: number
}

export type SpreadsheetStructuralOperationPayload =
  | SpreadsheetRowStructuralPayload
  | SpreadsheetColumnStructuralPayload
  | SpreadsheetResizeRowsPayload
  | SpreadsheetResizeColumnsPayload

export type SpreadsheetSetValidationPayload = {
  range: SpreadsheetRangeAddress
  validation_id?: string | null
}

export type SpreadsheetConditionalFormatRule = {
  id: string
  priority?: number
  ranges: SpreadsheetRangeAddress[]
  stop_if_true?: boolean
  kind?: string
  operator?: string
  formula1?: string | null
  formula2?: string | null
  style?: Record<string, unknown>
  format?: Record<string, unknown>
}

export type SpreadsheetUpsertConditionalFormatRulePayload = {
  rule: SpreadsheetConditionalFormatRule
}

export type SpreadsheetDeleteConditionalFormatRulePayload = {
  rule_id: string
}

export type SpreadsheetReorderConditionalFormatRulesPayload = {
  rule_ids: string[]
}

export type SpreadsheetClearConditionalFormatsPayload = {
  sheet_id?: string | null
  range?: SpreadsheetRangeAddress | null
}

export type SpreadsheetSparklineType = 'line' | 'column' | 'winLoss'
export type SpreadsheetSparklinePlotBy = 'rows' | 'columns'
export type SpreadsheetSparklineEmptyCellPolicy = 'zero' | 'gap' | 'span'
export type SpreadsheetSparklineAxisMode = 'individual' | 'group' | 'custom'

export type SpreadsheetSparklineColor = {
  rgb?: string
  theme?: number
  tint?: number
  indexed?: number
}

export type SpreadsheetSparklineColorSet = {
  series?: SpreadsheetSparklineColor | string
  negative?: SpreadsheetSparklineColor | string
  axis?: SpreadsheetSparklineColor | string
  markers?: SpreadsheetSparklineColor | string
  first?: SpreadsheetSparklineColor | string
  last?: SpreadsheetSparklineColor | string
  high?: SpreadsheetSparklineColor | string
  low?: SpreadsheetSparklineColor | string
}

export type SpreadsheetSparklineEntry = {
  location: SpreadsheetRangeAddress
  source_formula?: string | null
  source_range?: SpreadsheetRangeAddress | null
  date_range_formula?: string | null
  date_range?: SpreadsheetRangeAddress | null
  source_range_invalid?: boolean
  date_range_invalid?: boolean
  source_range_diagnostic?: string
  date_range_diagnostic?: string
}

export type SpreadsheetSparklineGroup = {
  id: string
  sheet_id: string
  type: SpreadsheetSparklineType
  plot_by: SpreadsheetSparklinePlotBy
  sparklines: SpreadsheetSparklineEntry[]
  date_axis?: boolean
  display_empty_cells_as?: SpreadsheetSparklineEmptyCellPolicy
  display_hidden?: boolean
  display_x_axis?: boolean
  right_to_left?: boolean
  min_axis?: SpreadsheetSparklineAxisMode
  max_axis?: SpreadsheetSparklineAxisMode
  manual_min?: number | null
  manual_max?: number | null
  line_weight?: number | null
  markers?: boolean
  high_point?: boolean
  low_point?: boolean
  first_point?: boolean
  last_point?: boolean
  negative_points?: boolean
  colors?: SpreadsheetSparklineColorSet
}

export type SpreadsheetUpsertSparklineGroupPayload = {
  group: SpreadsheetSparklineGroup
}

export type SpreadsheetDeleteSparklineGroupPayload = {
  group_id: string
}

export type SpreadsheetClearSparklinesPayload = {
  sheet_id?: string | null
  range?: SpreadsheetRangeAddress | null
}

export type SpreadsheetGroupSparklineLocationsPayload = {
  group_id: string
  new_group_id?: string | null
  locations?: SpreadsheetRangeAddress[]
}

export type SpreadsheetUngroupSparklineGroupPayload = {
  group_id: string
}

export type SpreadsheetDerivedArtifactKind = 'chart' | 'pivot'

export type SpreadsheetChartArtifactType =
  | 'column'
  | 'bar'
  | 'line'
  | 'area'
  | 'pie'
  | 'scatter'

export type SpreadsheetDerivedArtifact = {
  id: string
  sheet_id: string
  kind: SpreadsheetDerivedArtifactKind
  source_range: SpreadsheetRangeAddress
  placement: SpreadsheetRangeAddress
  title?: string | null
  chart_type?: SpreadsheetChartArtifactType | null
  metadata?: Record<string, unknown> | null
  pivot?: {
    row_fields?: string[]
    column_fields?: string[]
    value_fields?: string[]
    filter_fields?: string[]
    [key: string]: unknown
  } | null
  [key: string]: unknown
}

export type SpreadsheetUpsertDerivedArtifactPayload = {
  artifact: SpreadsheetDerivedArtifact
}

export type SpreadsheetDeleteDerivedArtifactPayload = {
  artifact_id: string
}

export type SpreadsheetDerivedArtifactsResponsePayload = SpreadsheetRoutedWorkerResponseMetadata & {
  derived_artifacts: SpreadsheetDerivedArtifact[]
}

export type SpreadsheetValidationRuleKind =
  | 'list'
  | 'wholeNumber'
  | 'decimal'
  | 'date'
  | 'time'
  | 'textLength'
  | 'custom'

export type SpreadsheetValidationRuleOperator =
  | 'between'
  | 'notBetween'
  | 'equal'
  | 'notEqual'
  | 'greaterThan'
  | 'lessThan'
  | 'greaterThanOrEqual'
  | 'lessThanOrEqual'

export type SpreadsheetValidationErrorStyle =
  | 'stop'
  | 'warning'
  | 'information'

export type SpreadsheetValidationRule = {
  id: string
  kind: SpreadsheetValidationRuleKind
  operator?: SpreadsheetValidationRuleOperator | null
  allow_blank?: boolean
  values?: string[] | null
  formula1?: string | null
  formula2?: string | null
  input_title?: string | null
  input_message?: string | null
  error_style?: SpreadsheetValidationErrorStyle | null
  error_title?: string | null
  error_message?: string | null
}

export type SpreadsheetUpsertValidationRulePayload = {
  rule: SpreadsheetValidationRule
}

export type SpreadsheetDeleteValidationRulePayload = {
  validation_id: string
}

export type SpreadsheetSetCommentPayload = {
  address: SpreadsheetCellAddress
  comment?: string | null
}

export type SpreadsheetSetHyperlinkPayload = {
  address: SpreadsheetCellAddress
  hyperlink?: string | null
}

export type SpreadsheetSetProtectionPayload = {
  range: SpreadsheetRangeAddress
  protection?: Record<string, unknown> | null
}

export type SpreadsheetSetSheetProtectionPayload = {
  sheet_id: string
  protection?: SpreadsheetSheetProtectionOptions | null
}

export type SpreadsheetDefineNamePayload = {
  name: string
  range?: SpreadsheetRangeAddress
  refers_to?: string
  scope?: SpreadsheetDefinedNameScope
  comment?: string | null
  hidden?: boolean
}

export type SpreadsheetDefinedNameScope =
  | { kind: 'workbook' }
  | { kind: 'sheet'; sheet_id: string }

export type SpreadsheetDefinedNameEntry = {
  name: string
  scope: SpreadsheetDefinedNameScope
  refers_to: string
  range?: SpreadsheetRangeAddress
  value?: SpreadsheetCellValue | string | number | boolean | null
  comment?: string | null
  hidden?: boolean
  error?: string | null
}

export type SpreadsheetUpsertDefinedNamePayload = {
  name: string
  scope: SpreadsheetDefinedNameScope
  refers_to: string
  comment?: string | null
  hidden?: boolean
}

export type SpreadsheetDeleteNamePayload = {
  name: string
  scope?: SpreadsheetDefinedNameScope
}

export type SpreadsheetSetFrozenPanesPayload = {
  sheet_id: string
  rows: number
  cols: number
}

export type SpreadsheetSetSheetHiddenPayload = {
  sheet_id: string
  hidden: boolean
}

export type SpreadsheetSetSheetTabColorPayload = {
  sheet_id: string
  tab_color?: Record<string, unknown> | null
}

export type SpreadsheetDuplicateSheetPayload = {
  sheet_id: string
  source_sheet_id: string
  name: string
  index?: number | null
}

export type SpreadsheetSetHiddenRowsPayload = {
  sheet_id: string
  row_index: number
  count: number
  hidden: boolean
}

export type SpreadsheetSetHiddenColumnsPayload = {
  sheet_id: string
  col_index: number
  count: number
  hidden: boolean
}

export type SpreadsheetAutoFilterOperator =
  | 'equals'
  | 'not_equals'
  | 'greater_than'
  | 'greater_than_or_equal'
  | 'less_than'
  | 'less_than_or_equal'
  | 'contains'
  | 'not_contains'
  | 'begins_with'
  | 'ends_with'

export type SpreadsheetAutoFilterConditionJoin = 'and' | 'or'

export type SpreadsheetAutoFilterCondition = {
  operator: SpreadsheetAutoFilterOperator
  value: string
}

export type SpreadsheetAutoFilterColumnCriteria = {
  col: number
  values?: string[]
  blank?: boolean
  conditions?: SpreadsheetAutoFilterCondition[]
  condition_join?: SpreadsheetAutoFilterConditionJoin
}

export type SpreadsheetAutoFilterSortCondition = {
  ref: SpreadsheetRangeAddress
  descending?: boolean
  sort_by?: string | null
  custom_list?: string | null
  dxf_id?: string | null
  icon_set?: string | null
  icon_id?: string | null
}

export type SpreadsheetAutoFilterSortState = {
  ref?: SpreadsheetRangeAddress | null
  case_sensitive?: boolean
  column_sort?: boolean
  conditions?: SpreadsheetAutoFilterSortCondition[]
}

export type SpreadsheetAutoFilter = {
  range: SpreadsheetRangeAddress
  columns?: SpreadsheetAutoFilterColumnCriteria[]
  sort_state?: SpreadsheetAutoFilterSortState | null
}

export type SpreadsheetSetAutoFilterPayload = {
  sheet_id: string
  range?: SpreadsheetRangeAddress | null
  columns?: SpreadsheetAutoFilterColumnCriteria[]
}

export type SpreadsheetTableColumnDefinition = {
  id?: string | null
  name?: string | null
  totals_row_label?: string | null
  totals_row_function?: string | null
  totals_row_formula?: string | null
  calculated_column_formula?: string | null
}

export type SpreadsheetTableDefinition = {
  id: string
  name: string
  range: SpreadsheetRangeAddress
  has_headers?: boolean
  show_filter_buttons?: boolean
  show_totals?: boolean
  style_id?: string | null
  show_first_column?: boolean
  show_last_column?: boolean
  show_row_stripes?: boolean
  show_column_stripes?: boolean
  columns?: SpreadsheetTableColumnDefinition[]
  auto_filter?: SpreadsheetAutoFilter | null
}

export type SpreadsheetTableStyleElement = {
  type: string
  size?: number | null
  dxf_id?: string | null
  style?: Record<string, unknown> | null
}

export type SpreadsheetTableStyleDefinition = {
  name: string
  is_table?: boolean
  is_pivot?: boolean
  elements?: SpreadsheetTableStyleElement[]
}

export type SpreadsheetUpsertTablePayload = {
  table: SpreadsheetTableDefinition
}

export type SpreadsheetDeleteTablePayload = {
  sheet_id: string
  table_id: string
}

export type SpreadsheetMaterializeDerivedRangePayload = {
  range: SpreadsheetRangeAddress
  values: SpreadsheetCellValue[][]
  source_id?: string | null
  source_kind?: string | null
}

export type SpreadsheetEngineDelta = {
  workbook_id: string
  workbook_revision: number
  changed_cells: SpreadsheetCellAddress[]
  changed_ranges: SpreadsheetRangeAddress[]
  invalidated_chunk_ids: string[]
  recalc_status: 'idle' | 'dirty' | 'calculating' | 'blocked'
  spill_changes: SpreadsheetRangeAddress[]
  external_changes: SpreadsheetExternalFunctionRequest[]
  diagnostics: SpreadsheetEngineDiagnostic[]
  ui_invalidations: Array<'viewport' | 'formula_bar' | 'sheet_tabs' | 'format_toolbar' | 'presence'>
}

export type SpreadsheetEngineDiagnostic = {
  workbook_id: string
  sheet_id?: string | null
  request_id?: string | null
  revision?: number | null
  chunk_id?: string | null
  operation_type?: SpreadsheetEngineOperationType | null
  category: 'engine' | 'worker' | 'document_session' | 'external_function'
  message: string
  duration_ms?: number | null
}

export const buildSpreadsheetEngineDelta = (input: {
  workbook_id: string
  workbook_revision: number
  changed_cells?: SpreadsheetCellAddress[]
  changed_ranges?: SpreadsheetRangeAddress[]
  invalidated_chunk_ids?: string[]
  recalc_status?: SpreadsheetEngineDelta['recalc_status']
  spill_changes?: SpreadsheetRangeAddress[]
  external_changes?: SpreadsheetExternalFunctionRequest[]
  diagnostics?: SpreadsheetEngineDiagnostic[]
  ui_invalidations?: SpreadsheetEngineDelta['ui_invalidations']
}): SpreadsheetEngineDelta => ({
  workbook_id: String(input.workbook_id || '').trim(),
  workbook_revision: normalizeSeq(input.workbook_revision),
  changed_cells: Array.isArray(input.changed_cells) ? input.changed_cells : [],
  changed_ranges: Array.isArray(input.changed_ranges) ? input.changed_ranges : [],
  invalidated_chunk_ids: Array.isArray(input.invalidated_chunk_ids)
    ? input.invalidated_chunk_ids.map((chunkId) => String(chunkId || '').trim()).filter(Boolean)
    : [],
  recalc_status: input.recalc_status ?? 'idle',
  spill_changes: Array.isArray(input.spill_changes) ? input.spill_changes : [],
  external_changes: Array.isArray(input.external_changes) ? input.external_changes : [],
  diagnostics: Array.isArray(input.diagnostics) ? input.diagnostics : [],
  ui_invalidations: Array.isArray(input.ui_invalidations) ? input.ui_invalidations : [],
})

export type SpreadsheetExternalFunctionRequest = {
  request_id: string
  workbook_id: string
  sheet_id: string
  address: SpreadsheetCellAddress
  source_id: string
  function_name: 'SQL'
  arguments: Array<
    | { kind: 'query'; sql: string }
    | { kind: 'range'; range: SpreadsheetRangeAddress; mode: SpreadsheetRangeReadMode }
  >
  created_at_ms: number
  timeout_ms: number
  max_rows: number
  max_cols: number
  max_bytes: number
}

export type SpreadsheetExternalRequestedResponsePayload = SpreadsheetRoutedWorkerResponseMetadata & {
  request: SpreadsheetExternalFunctionRequest
}

export type SpreadsheetExternalFunctionResult = {
  request_id: string
  workbook_id: string
  sheet_id: string
  address: SpreadsheetCellAddress
  status: 'ok' | 'error' | 'cancelled'
  values?: SpreadsheetCellValue[][]
  error?: { code: SpreadsheetEngineErrorCode; message: string; category: string }
  completed_at_ms: number
}

export type SpreadsheetExcelWorkbookIoFormat = 'xlsx' | 'xlsm'
export type SpreadsheetExcelUnsupportedArtifactPolicy =
  | 'preserve_non_authoritative'
  | 'drop_with_warning'
  | 'reject'

export type SpreadsheetExcelIoWarning = {
  code: string
  severity: 'info' | 'warning' | 'error'
  message: string
  sheet_name?: string | null
  part_name?: string | null
}

export type SpreadsheetExcelOoxmlArtifactManifestEntry = {
  part_name: string
  content_type?: string | null
  relationship_type?: string | null
  source_part_name?: string | null
  relationship_id?: string | null
  relationship_target?: string | null
  relationship_target_mode?: string | null
  marker_attributes?: Record<string, string>
  byte_length?: number | null
  checksum_crc32?: string | null
  payload_base64?: string | null
  relationships?: SpreadsheetExcelOoxmlArtifactRelationship[]
  preservation: 'modeled_engine_state' | 'non_authoritative_attachment' | 'dropped'
}

export type SpreadsheetExcelOoxmlArtifactRelationship = {
  id: string
  type: string
  target: string
  target_mode?: string | null
}

export type SpreadsheetExcelImportOptions = {
  unsupported_artifact_policy?: SpreadsheetExcelUnsupportedArtifactPolicy
  include_preview_values?: boolean
}

export type SpreadsheetExcelImportPreviewPayload = {
  format: SpreadsheetExcelWorkbookIoFormat
  file_name: string
  content_type?: string | null
  package_base64: string
  byte_length?: number
  options?: SpreadsheetExcelImportOptions
}

export type SpreadsheetExcelImportWorkbookPayload = SpreadsheetExcelImportPreviewPayload & {
  import_mode?: 'new_workbook' | 'replace_workbook'
  target_workbook_id?: string | null
  actor_client_id?: string
  created_at_ms?: number
}

export type SpreadsheetExcelExportWorkbookPayload = {
  format: SpreadsheetExcelWorkbookIoFormat
  file_name?: string | null
  include_calculated_values?: boolean
  preserve_non_authoritative_artifacts?: boolean
  actor_client_id?: string
  created_at_ms?: number
}

export type SpreadsheetExcelExportSnapshotPayload = SpreadsheetExcelExportWorkbookPayload & {
  snapshot: SpreadsheetWorkbookSnapshot
}

export type SpreadsheetRestoreWorkbookSnapshotPayload = {
  snapshot: SpreadsheetWorkbookSnapshot
  actor_client_id?: string
  created_at_ms?: number
}

export type SpreadsheetWorkbookSnapshotResponsePayload = SpreadsheetRoutedWorkerResponseMetadata & {
  workbook_id: string
  workbook_revision: number
  snapshot: SpreadsheetWorkbookSnapshot
}

export type SpreadsheetExcelRoundTripValidationPayload = SpreadsheetExcelImportPreviewPayload

export type SpreadsheetExcelImportPreviewSheet = {
  name: string
  row_count: number
  col_count: number
  hidden?: boolean
  table_count?: number
  formula_count?: number
  warning_count?: number
}

export type SpreadsheetExcelImportPreviewResponsePayload = SpreadsheetRoutedWorkerResponseMetadata & {
  format: SpreadsheetExcelWorkbookIoFormat
  file_name: string
  workbook_name?: string | null
  sheets: SpreadsheetExcelImportPreviewSheet[]
  defined_name_count?: number
  style_count?: number
  warnings: SpreadsheetExcelIoWarning[]
  advanced_ooxml_artifacts?: SpreadsheetExcelOoxmlArtifactManifestEntry[]
}

export type SpreadsheetExcelImportResponsePayload = SpreadsheetRoutedWorkerResponseMetadata & {
  workbook_id: string
  workbook_revision: number
  snapshot?: SpreadsheetWorkbookSnapshot | null
  engine_ops?: SpreadsheetEngineOperationEnvelope[]
  warnings: SpreadsheetExcelIoWarning[]
  advanced_ooxml_artifacts?: SpreadsheetExcelOoxmlArtifactManifestEntry[]
}

export type SpreadsheetExcelExportResponsePayload = SpreadsheetRoutedWorkerResponseMetadata & {
  format: SpreadsheetExcelWorkbookIoFormat
  file_name: string
  content_type: string
  package_base64: string
  byte_length: number
  warnings: SpreadsheetExcelIoWarning[]
  advanced_ooxml_artifacts?: SpreadsheetExcelOoxmlArtifactManifestEntry[]
}

export type SpreadsheetRestoreWorkbookSnapshotResponsePayload = SpreadsheetRoutedWorkerResponseMetadata & {
  workbook_id: string
  workbook_revision: number
  sheet_count: number
}

export type SpreadsheetExcelRoundTripValidationResponsePayload = SpreadsheetRoutedWorkerResponseMetadata & {
  ok: boolean
  semantic_diffs: string[]
  import_warnings: SpreadsheetExcelIoWarning[]
  export_warnings: SpreadsheetExcelIoWarning[]
  advanced_ooxml_artifacts?: SpreadsheetExcelOoxmlArtifactManifestEntry[]
}

export const SPREADSHEET_ENGINE_WORKER_COMMANDS = [
  'workbook.open',
  'workbook.close',
  'workbook.snapshot',
  'workbook.restoreSnapshot',
  'workbook.getManifest',
  'workbook.getNamedRanges',
  'workbook.getDefinedNames',
  'workbook.getValidationRules',
  'workbook.getConditionalFormatRules',
  'workbook.getSparklineGroups',
  'workbook.getDerivedArtifacts',
  'formula.evaluateDraft',
  'sheet.getValidationDropdownOptions',
  'sheet.getValidationIssues',
  'sheet.applyOps',
  'sheet.list',
  'sheet.getViewport',
  'sheet.getRange',
  'sheet.copyRange',
  'sheet.copyRangeTo',
  'sheet.copyRangeFormatsTo',
  'sheet.pasteTsv',
  'sheet.pasteSpecial',
  'sheet.fillRange',
  'sheet.findReplace',
  'sheet.removeDuplicates',
  'sheet.sortRange',
  'sheet.moveRange',
  'sheet.recalc',
  'sheet.recalcIncremental',
  'sheet.undo',
  'sheet.redo',
  'sheet.registerExternalSource',
  'excel.importPreview',
  'excel.importWorkbook',
  'excel.exportWorkbook',
  'excel.exportSnapshot',
  'excel.validateRoundTrip',
  'external.applyResult',
  'presence.update',
] as const

export const SPREADSHEET_ENGINE_WORKER_RESPONSES = [
  'workbook.opened',
  'workbook.snapshot',
  'workbook.restored',
  'workbook.namedRanges',
  'workbook.definedNames',
  'workbook.validationRules',
  'workbook.conditionalFormatRules',
  'workbook.sparklineGroups',
  'workbook.derivedArtifacts',
  'formula.draftEvaluation',
  'sheet.validationDropdownOptions',
  'sheet.validationIssues',
  'sheet.delta',
  'sheet.list',
  'sheet.viewport',
  'sheet.range',
  'sheet.copyPayload',
  'sheet.calcStatus',
  'excel.importPreviewed',
  'excel.imported',
  'excel.exported',
  'excel.roundTripValidated',
  'external.requested',
  'external.cancelled',
  'diagnostics.updated',
  'error',
] as const

export type SpreadsheetEngineWorkerCommandName = typeof SPREADSHEET_ENGINE_WORKER_COMMANDS[number]
export type SpreadsheetEngineWorkerResponseName = typeof SPREADSHEET_ENGINE_WORKER_RESPONSES[number]

export type SpreadsheetEngineWorkerCommand<TPayload = unknown> = {
  protocolVersion: typeof SPREADSHEET_ENGINE_PROTOCOL_VERSION
  command: SpreadsheetEngineWorkerCommandName
  requestId: string
  workbookId: string
  sheetId?: string | null
  baseRevision: number
  clientId: string
  payload: TPayload
}

export type SpreadsheetEngineWorkerResponse<TPayload = unknown> = {
  protocolVersion: typeof SPREADSHEET_ENGINE_PROTOCOL_VERSION
  response: SpreadsheetEngineWorkerResponseName
  requestId: string
  workbookId: string
  sheetId?: string | null
  workbookRevision?: number | null
  payload: TPayload
}

export type SpreadsheetSheetListEntry = {
  id: number
  name: string
  row_count?: number
  col_count?: number
  row_metadata?: Record<string, Record<string, unknown>>
  column_metadata?: Record<string, Record<string, unknown>>
  frozen_panes?: { rows: number; cols: number } | null
  hidden?: boolean
  sheet_protection?: SpreadsheetSheetProtectionOptions | null
  auto_filter?: SpreadsheetAutoFilter | null
  tables?: Record<string, SpreadsheetTableDefinition>
}

export type SpreadsheetSheetListResponsePayload = SpreadsheetRoutedWorkerResponseMetadata & {
  sheets: SpreadsheetSheetListEntry[]
  styles?: SpreadsheetStyleSidecars
}

export type SpreadsheetNamedRangeEntry = {
  name: string
  range: SpreadsheetRangeAddress
}

export type SpreadsheetNamedRangesResponsePayload = SpreadsheetRoutedWorkerResponseMetadata & {
  named_ranges: SpreadsheetNamedRangeEntry[]
  sheets: SpreadsheetSheetListEntry[]
}

export type SpreadsheetDefinedNamesResponsePayload = SpreadsheetRoutedWorkerResponseMetadata & {
  defined_names: SpreadsheetDefinedNameEntry[]
  sheets: SpreadsheetSheetListEntry[]
}

export type SpreadsheetValidationRulesResponsePayload = SpreadsheetRoutedWorkerResponseMetadata & {
  validation_rules: SpreadsheetValidationRule[]
}

export type SpreadsheetConditionalFormatRulesResponsePayload = SpreadsheetRoutedWorkerResponseMetadata & {
  conditional_format_rules: SpreadsheetConditionalFormatRule[]
}

export type SpreadsheetSparklineGroupsResponsePayload = SpreadsheetRoutedWorkerResponseMetadata & {
  sparkline_groups: SpreadsheetSparklineGroup[]
}

export type SpreadsheetValidationDropdownOptionsPayload = {
  address: SpreadsheetCellAddress
}

export type SpreadsheetValidationDropdownOptionsResponsePayload = SpreadsheetRoutedWorkerResponseMetadata & {
  workbook_id: string
  workbook_revision: number
  sheet_id: string
  address: SpreadsheetCellAddress
  validation_id?: string | null
  rule?: SpreadsheetValidationRule | null
  source_formula?: string | null
  source_ranges?: SpreadsheetRangeAddress[]
  options: string[]
  error?: string | null
}

export type SpreadsheetValidationIssuesPayload = {
  range?: SpreadsheetRangeAddress | null
}

export type SpreadsheetValidationIssue = {
  address: SpreadsheetCellAddress
  validation_id: string
  rule?: SpreadsheetValidationRule | null
  raw_input: string
  error_style?: SpreadsheetValidationErrorStyle | null
  error_title?: string | null
  message: string
}

export type SpreadsheetValidationIssuesResponsePayload = SpreadsheetRoutedWorkerResponseMetadata & {
  workbook_id: string
  workbook_revision: number
  sheet_id: string
  range?: SpreadsheetRangeAddress | null
  issues: SpreadsheetValidationIssue[]
}

export type SpreadsheetDocumentSessionUpdate = {
  document_protocol_version: typeof DOCUMENT_SESSION_PROTOCOL_VERSION
  doc_scope: DocumentSessionDocScope
  chunk_id?: string | null
  engine_ops: SpreadsheetEngineOperationEnvelope[]
}

export const SPREADSHEET_ENGINE_OPS_DOC_MAP = 'spreadsheet_engine_ops' as const
export const SPREADSHEET_WORKBOOK_SNAPSHOT_DOC_MAP = 'spreadsheet_workbook_snapshot' as const

export const SPREADSHEET_ENGINE_EXCEL_MVP_FUNCTIONS = {
  math_stat: [
    'SUM', 'AVERAGE', 'MIN', 'MAX', 'COUNT', 'COUNTA',
    'COUNTBLANK',
    'SUMIF', 'SUMIFS', 'COUNTIF', 'COUNTIFS', 'AVERAGEIF', 'AVERAGEIFS',
    'MINIFS', 'MAXIFS',
    'SUBTOTAL', 'AGGREGATE',
    'ABS', 'ROUND', 'FLOOR', 'CEILING', 'INT', 'TRUNC', 'MOD', 'POWER', 'SQRT', 'MROUND',
    'ROUNDUP', 'ROUNDDOWN', 'PRODUCT', 'SUMPRODUCT', 'MEDIAN',
    'STDEV.S', 'STDEV.P', 'VAR.S', 'VAR.P',
    'LARGE', 'SMALL', 'RANK', 'RANK.EQ',
    'PERCENTILE', 'PERCENTILE.INC', 'PERCENTILE.EXC',
    'QUARTILE', 'QUARTILE.INC', 'QUARTILE.EXC',
    'MODE', 'MODE.SNGL', 'RAND', 'RANDBETWEEN',
  ],
  logic: ['IF', 'IFERROR', 'IFNA', 'IFS', 'SWITCH', 'CHOOSE', 'AND', 'OR', 'NOT', 'XOR', 'LET'],
  info: ['ISBLANK', 'ISNUMBER', 'ISTEXT', 'ISERROR', 'ISNA', 'ERROR.TYPE', 'N', 'T'],
  text: [
    'LEN', 'LEFT', 'RIGHT', 'MID', 'CONCAT', 'TEXTJOIN',
    'TEXT', 'VALUE', 'TRIM', 'UPPER', 'LOWER', 'PROPER', 'FIND', 'SEARCH',
    'REPLACE', 'SUBSTITUTE', 'EXACT', 'TEXTBEFORE', 'TEXTAFTER', 'TEXTSPLIT',
  ],
  date_time: [
    'DATE', 'TODAY', 'NOW', 'YEAR', 'MONTH', 'DAY', 'HOUR', 'MINUTE',
    'SECOND', 'TIME', 'WEEKDAY', 'WEEKNUM',
    'DATEDIF', 'EDATE', 'EOMONTH', 'NETWORKDAYS', 'WORKDAY',
  ],
  lookup: [
    'INDEX', 'MATCH', 'XMATCH', 'XLOOKUP', 'VLOOKUP', 'HLOOKUP', 'LOOKUP',
    'OFFSET', 'INDIRECT', 'ADDRESS', 'ROW', 'COLUMN', 'ROWS', 'COLUMNS',
  ],
  dynamic_arrays: [
    'SEQUENCE', 'TRANSPOSE', 'FILTER', 'UNIQUE', 'SORT', 'SORTBY',
    'TAKE', 'DROP', 'CHOOSECOLS', 'CHOOSEROWS', 'HSTACK', 'VSTACK',
    'TOROW', 'TOCOL', 'WRAPROWS', 'WRAPCOLS',
  ],
  financial: ['PMT', 'RATE'],
  engineering: ['CONVERT', 'DEC2BIN'],
  database: ['DSUM', 'DCOUNT'],
  external: ['SQL'],
  explicitly_out_of_mvp: [],
} as const

export const SPREADSHEET_ENGINE_EXCEL_MVP_REFERENCES = [
  'relative',
  'absolute',
  'cross_sheet',
  'ranges',
  'whole_rows',
  'whole_columns',
  'named_ranges',
] as const

export const SPREADSHEET_ENGINE_CREATOR_PARITY_CHECKLIST = [
  'keyboard_navigation',
  'selection_ranges',
  'formula_bar_editing',
  'inline_cell_editing',
  'copy_cut_paste',
  'quoted_multiline_tsv',
  'fill_handle',
  'context_menu_actions',
  'undo_redo',
  'sheet_tabs',
  'add_delete_rename_move_sheets',
  'duplicate_sheets',
  'hide_unhide_sheets',
  'column_row_resizing',
  'row_column_insert_delete',
  'clear_contents',
  'toolbar_formatting',
  'paste_special_values_styles',
  'validation_comment_hyperlink_protection_metadata',
  'frozen_panes_hidden_rows_columns',
] as const

const normalizeSeq = (value: unknown): number => {
  const next = Number(value)
  if (!Number.isFinite(next) || next < 0) return 0
  return Math.floor(next)
}

const isSpreadsheetProtocolRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value))

export const isSpreadsheetOperationType = (value: string): value is SpreadsheetEngineOperationType =>
  (SPREADSHEET_ENGINE_OPERATION_TYPES as readonly string[]).includes(value)

export const isSpreadsheetEngineOperationEnvelope = (value: unknown): value is SpreadsheetEngineOperationEnvelope => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    String(value.op_id ?? '').trim().length > 0 &&
    isSpreadsheetOperationType(String(value.op_type ?? '')) &&
    String(value.workbook_id ?? '').trim().length > 0 &&
    (value.sheet_id == null || typeof value.sheet_id === 'string') &&
    Number.isFinite(Number(value.base_revision)) &&
    String(value.actor_client_id ?? '').trim().length > 0 &&
    Number.isFinite(Number(value.created_at_ms)) &&
    Object.prototype.hasOwnProperty.call(value, 'payload')
  )
}

const hasOwnSpreadsheetStyleKey = (style: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(style, key)

const spreadsheetStyleSection = (
  style: Record<string, unknown>,
  key: string,
): Record<string, unknown> | null => (
  isSpreadsheetProtocolRecord(style[key]) ? style[key] : null
)

const normalizeNonEmptySpreadsheetStyleString = (value: unknown): string | null => {
  const text = typeof value === 'string' ? value.trim() : ''
  return text ? text : null
}

const copySpreadsheetStyleMirror = (
  style: Record<string, unknown>,
  mirrorKey: string,
  value: unknown,
): void => {
  if (hasOwnSpreadsheetStyleKey(style, mirrorKey) || value == null) return
  if (typeof value === 'string') {
    const normalized = normalizeNonEmptySpreadsheetStyleString(value)
    if (normalized) style[mirrorKey] = normalized
    return
  }
  style[mirrorKey] = value
}

export const normalizeSpreadsheetStyleProjectionMirrors = (
  rawStyle: Record<string, unknown>,
): Record<string, unknown> => {
  const style: Record<string, unknown> = { ...rawStyle }
  const font = spreadsheetStyleSection(style, 'font')
  if (font) {
    copySpreadsheetStyleMirror(style, 'bold', font.bold)
    copySpreadsheetStyleMirror(style, 'italic', font.italic)
    copySpreadsheetStyleMirror(style, 'underline', font.underline)
    copySpreadsheetStyleMirror(style, 'strikethrough', font.strikethrough)
    copySpreadsheetStyleMirror(style, 'fontSize', font.size)
    copySpreadsheetStyleMirror(style, 'fontFamily', font.family)
    copySpreadsheetStyleMirror(style, 'textColor', font.color)
  }

  const fill = spreadsheetStyleSection(style, 'fill')
  if (fill && !hasOwnSpreadsheetStyleKey(style, 'backgroundColor')) {
    const foreground = normalizeNonEmptySpreadsheetStyleString(fill.foregroundColor)
    const background = normalizeNonEmptySpreadsheetStyleString(fill.backgroundColor)
    if (foreground || background) style.backgroundColor = foreground ?? background
  }

  const alignment = spreadsheetStyleSection(style, 'alignment')
  if (alignment) {
    copySpreadsheetStyleMirror(style, 'align', alignment.horizontal)
    copySpreadsheetStyleMirror(style, 'verticalAlign', alignment.vertical)
    copySpreadsheetStyleMirror(style, 'wrapText', alignment.wrapText)
    copySpreadsheetStyleMirror(style, 'shrinkToFit', alignment.shrinkToFit)
    copySpreadsheetStyleMirror(style, 'indent', alignment.indent)
    copySpreadsheetStyleMirror(style, 'textRotation', alignment.textRotation)
  }

  return style
}

export const normalizeSpreadsheetStyleSidecars = (value: unknown): SpreadsheetStyleSidecars | null => {
  if (!isSpreadsheetProtocolRecord(value)) return null
  const styles: SpreadsheetStyleSidecars = {}
  for (const [rawStyleId, rawStyle] of Object.entries(value)) {
    const styleId = String(rawStyleId ?? '').trim()
    if (!styleId || !isSpreadsheetProtocolRecord(rawStyle)) continue
    styles[styleId] = normalizeSpreadsheetStyleProjectionMirrors(rawStyle)
  }
  return Object.keys(styles).length > 0 ? styles : null
}

export const getSpreadsheetOperationStyleSidecars = (
  operation: SpreadsheetEngineOperationEnvelope,
): SpreadsheetStyleSidecars | null => {
  if (!isSpreadsheetProtocolRecord(operation.payload)) return null
  return normalizeSpreadsheetStyleSidecars(operation.payload.styles)
}

const isSpreadsheetCellAddress = (value: unknown): value is SpreadsheetCellAddress => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    String(value.sheet_id ?? '').trim().length > 0 &&
    Number.isFinite(Number(value.row)) &&
    Number.isFinite(Number(value.col))
  )
}

const isSpreadsheetRangeAddress = (value: unknown): value is SpreadsheetRangeAddress => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    String(value.sheet_id ?? '').trim().length > 0 &&
    Number.isFinite(Number(value.start_row)) &&
    Number.isFinite(Number(value.start_col)) &&
    Number.isFinite(Number(value.end_row)) &&
    Number.isFinite(Number(value.end_col))
  )
}

const isSpreadsheetNamedRangeSnapshotValue = (value: unknown): boolean => {
  if (isSpreadsheetRangeAddress(value)) return true
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (typeof value.error === 'string' && value.error.trim().length > 0) return true
  return typeof value.refers_to === 'string' && value.refers_to.trim().length > 0
}

const isSpreadsheetWorkerRangeAddress = (value: unknown): value is SpreadsheetRangeAddress => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    String(value.sheet_id ?? '').trim().length > 0 &&
    isNonNegativeIntegerNumber(value.start_row) &&
    isNonNegativeIntegerNumber(value.start_col) &&
    isNonNegativeIntegerNumber(value.end_row) &&
    isNonNegativeIntegerNumber(value.end_col)
  )
}

const isOptionalNonEmptyString = (value: unknown): boolean =>
  value == null || (typeof value === 'string' && value.trim().length > 0)

const isOptionalRelationshipSourcePartName = (value: unknown): boolean =>
  value == null || value === '' || (typeof value === 'string' && value.trim().length > 0)

const isOptionalFiniteNumber = (value: unknown): boolean =>
  value == null || (typeof value === 'number' && Number.isFinite(value))

const isOptionalBoolean = (value: unknown): boolean =>
  value == null || typeof value === 'boolean'

const isOptionalSpreadsheetStyleSidecars = (value: unknown): value is SpreadsheetStyleSidecars | undefined => {
  if (value == null) return true
  if (!isSpreadsheetProtocolRecord(value)) return false
  return Object.entries(value).every(([styleId, style]) => (
    String(styleId ?? '').trim().length > 0 &&
    isSpreadsheetProtocolRecord(style)
  ))
}

const isSpreadsheetRecordOfRecords = (value: unknown): value is Record<string, Record<string, unknown>> => (
  isSpreadsheetProtocolRecord(value) &&
  Object.values(value).every(isSpreadsheetProtocolRecord)
)

const isSpreadsheetSheetAxisMetadataMap = (value: unknown): value is Record<string, Record<string, unknown>> => (
  isSpreadsheetRecordOfRecords(value) &&
  Object.entries(value).every(([axisIndex, metadata]) => {
    if (!isNonNegativeIntegerNumber(Number(axisIndex))) return false
    return (
      isOptionalNonNegativeFiniteNumber(metadata.height_px) &&
      isOptionalNonNegativeFiniteNumber(metadata.width_px) &&
      isOptionalBoolean(metadata.hidden) &&
      isOptionalBoolean(metadata.collapsed) &&
      (metadata.outline_level == null || isNonNegativeIntegerNumber(metadata.outline_level)) &&
      isOptionalNonEmptyString(metadata.style_id)
    )
  })
)

const isRecordOfStrings = (value: unknown): value is Record<string, string> => (
  isSpreadsheetProtocolRecord(value) &&
  Object.values(value).every((entry) => typeof entry === 'string')
)

const isSpreadsheetSheetOoxmlRawElement = (value: unknown): value is SpreadsheetSheetOoxmlRawElement => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    typeof value.raw_xml === 'string' &&
    value.raw_xml.trim().length > 0 &&
    (value.attributes == null || isRecordOfStrings(value.attributes))
  )
}

const isSpreadsheetSheetOoxmlExtension = (value: unknown): value is SpreadsheetSheetOoxmlExtension => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isOptionalNonEmptyString(value.uri) &&
    typeof value.raw_xml === 'string' &&
    value.raw_xml.trim().length > 0
  )
}

const isSpreadsheetSheetOoxmlExtensionList = (value: unknown): value is SpreadsheetSheetOoxmlExtensionList => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    typeof value.raw_xml === 'string' &&
    value.raw_xml.trim().length > 0 &&
    (value.extensions == null || (
      Array.isArray(value.extensions) &&
      value.extensions.every(isSpreadsheetSheetOoxmlExtension)
    ))
  )
}

const isSpreadsheetSheetOoxmlMetadata = (value: unknown): value is SpreadsheetSheetOoxmlMetadata => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    (value.phonetic_properties == null || isSpreadsheetSheetOoxmlRawElement(value.phonetic_properties)) &&
    (value.smart_tags == null || isSpreadsheetSheetOoxmlRawElement(value.smart_tags)) &&
    (value.scenarios == null || isSpreadsheetSheetOoxmlRawElement(value.scenarios)) &&
    (value.extension_list == null || isSpreadsheetSheetOoxmlExtensionList(value.extension_list))
  )
}

const isSpreadsheetSheetView = (value: unknown): value is SpreadsheetSheetView => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  const optionalPositiveInteger = (entry: unknown): boolean => entry == null || isPositiveIntegerNumber(entry)
  return (
    (value.workbook_view_id == null || isNonNegativeIntegerNumber(value.workbook_view_id)) &&
    isOptionalBoolean(value.show_grid_lines) &&
    isOptionalBoolean(value.show_row_col_headers) &&
    isOptionalBoolean(value.show_formulas) &&
    isOptionalBoolean(value.right_to_left) &&
    isOptionalBoolean(value.tab_selected) &&
    optionalPositiveInteger(value.zoom_scale) &&
    optionalPositiveInteger(value.zoom_scale_normal) &&
    isOptionalNonEmptyString(value.top_left_cell) &&
    isOptionalNonEmptyString(value.active_cell) &&
    isOptionalNonEmptyString(value.selection_sqref)
  )
}

const isSpreadsheetSheetCustomView = (value: unknown): value is SpreadsheetSheetCustomView => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  const optionalPositiveInteger = (entry: unknown): boolean => entry == null || isPositiveIntegerNumber(entry)
  return (
    isNullableStringField(value.guid) &&
    isNullableStringField(value.name) &&
    optionalPositiveInteger(value.scale) &&
    (value.color_id == null || isNonNegativeIntegerNumber(value.color_id)) &&
    isOptionalBoolean(value.show_page_breaks) &&
    isOptionalBoolean(value.show_formulas) &&
    isOptionalBoolean(value.show_grid_lines) &&
    isOptionalBoolean(value.show_row_col_headers) &&
    isOptionalBoolean(value.outline_symbols) &&
    isOptionalBoolean(value.zero_values) &&
    isOptionalBoolean(value.fit_to_page) &&
    isOptionalBoolean(value.print_area) &&
    isOptionalBoolean(value.filter) &&
    isOptionalBoolean(value.hidden_rows) &&
    isOptionalBoolean(value.hidden_columns) &&
    isNullableStringField(value.state) &&
    isNullableStringField(value.top_left_cell) &&
    isNullableStringField(value.active_cell) &&
    isNullableStringField(value.selection_sqref)
  )
}

const isOptionalNonNegativeFiniteNumber = (value: unknown): boolean => (
  value == null ||
  (typeof value === 'number' && Number.isFinite(value) && value >= 0)
)

const isSpreadsheetSheetFormatProperties = (value: unknown): value is SpreadsheetSheetFormatProperties => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isOptionalNonNegativeFiniteNumber(value.default_row_height_px) &&
    isOptionalNonNegativeFiniteNumber(value.default_col_width_px) &&
    (value.base_col_width == null || isNonNegativeIntegerNumber(value.base_col_width)) &&
    isOptionalBoolean(value.zero_height) &&
    isOptionalBoolean(value.thick_top) &&
    isOptionalBoolean(value.thick_bottom) &&
    (value.outline_level_row == null || isNonNegativeIntegerNumber(value.outline_level_row)) &&
    (value.outline_level_col == null || isNonNegativeIntegerNumber(value.outline_level_col)) &&
    isOptionalBoolean(value.outline_summary_below) &&
    isOptionalBoolean(value.outline_summary_right) &&
    isOptionalBoolean(value.show_outline_symbols) &&
    isOptionalBoolean(value.apply_outline_styles)
  )
}

const isSpreadsheetSheetCalculationProperties = (value: unknown): value is SpreadsheetSheetCalculationProperties => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return isOptionalBoolean(value.full_calc_on_load)
}

const isSpreadsheetSheetPageBreak = (value: unknown): value is SpreadsheetSheetPageBreak => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isNonNegativeIntegerNumber(value.id) &&
    (value.min == null || isNonNegativeIntegerNumber(value.min)) &&
    (value.max == null || isNonNegativeIntegerNumber(value.max)) &&
    isOptionalBoolean(value.manual)
  )
}

const isSpreadsheetSheetIgnoredError = (value: unknown): value is SpreadsheetSheetIgnoredError => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    typeof value.range === 'string' &&
    value.range.trim().length > 0 &&
    isOptionalBoolean(value.eval_error) &&
    isOptionalBoolean(value.two_digit_text_year) &&
    isOptionalBoolean(value.number_stored_as_text) &&
    isOptionalBoolean(value.formula) &&
    isOptionalBoolean(value.formula_range) &&
    isOptionalBoolean(value.unlocked_formula) &&
    isOptionalBoolean(value.empty_cell_reference) &&
    isOptionalBoolean(value.list_data_validation) &&
    isOptionalBoolean(value.calculated_column)
  )
}

const isSpreadsheetSheetProtectedRange = (value: unknown): value is SpreadsheetSheetProtectedRange => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    typeof value.range === 'string' &&
    value.range.trim().length > 0 &&
    isNullableStringField(value.name) &&
    isNullableStringField(value.security_descriptor) &&
    isNullableStringField(value.password_hash) &&
    isNullableStringField(value.algorithm) &&
    isNullableStringField(value.hash_value) &&
    isNullableStringField(value.salt_value) &&
    (
      value.spin_count == null ||
      (typeof value.spin_count === 'string' && value.spin_count.trim().length > 0) ||
      isNonNegativeIntegerNumber(value.spin_count)
    )
  )
}

const isSpreadsheetSheetPrintSettings = (value: unknown): value is SpreadsheetSheetPrintSettings => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  const printOptions = value.print_options
  if (printOptions != null) {
    if (!isSpreadsheetProtocolRecord(printOptions)) return false
    if (
      !isOptionalBoolean(printOptions.horizontal_centered) ||
      !isOptionalBoolean(printOptions.vertical_centered) ||
      !isOptionalBoolean(printOptions.headings) ||
      !isOptionalBoolean(printOptions.grid_lines) ||
      !isOptionalBoolean(printOptions.grid_lines_set)
    ) return false
  }
  const pageMargins = value.page_margins
  if (pageMargins != null) {
    if (!isSpreadsheetProtocolRecord(pageMargins)) return false
    if (
      !isOptionalNonNegativeFiniteNumber(pageMargins.left) ||
      !isOptionalNonNegativeFiniteNumber(pageMargins.right) ||
      !isOptionalNonNegativeFiniteNumber(pageMargins.top) ||
      !isOptionalNonNegativeFiniteNumber(pageMargins.bottom) ||
      !isOptionalNonNegativeFiniteNumber(pageMargins.header) ||
      !isOptionalNonNegativeFiniteNumber(pageMargins.footer)
    ) return false
  }
  const pageSetup = value.page_setup
  if (pageSetup != null) {
    if (!isSpreadsheetProtocolRecord(pageSetup)) return false
    if (
      !isOptionalNonNegativeFiniteNumber(pageSetup.paper_size) ||
      !isOptionalNonNegativeFiniteNumber(pageSetup.scale) ||
      !isOptionalNonNegativeFiniteNumber(pageSetup.fit_to_width) ||
      !isOptionalNonNegativeFiniteNumber(pageSetup.fit_to_height) ||
      !isOptionalBoolean(pageSetup.fit_to_page) ||
      !isOptionalNonNegativeFiniteNumber(pageSetup.horizontal_dpi) ||
      !isOptionalNonNegativeFiniteNumber(pageSetup.vertical_dpi) ||
      !isOptionalNonNegativeFiniteNumber(pageSetup.first_page_number) ||
      !isOptionalBoolean(pageSetup.use_first_page_number) ||
      !isOptionalBoolean(pageSetup.black_and_white) ||
      !isOptionalBoolean(pageSetup.draft) ||
      !isNullableStringField(pageSetup.cell_comments) ||
      !isNullableStringField(pageSetup.errors)
    ) return false
    if (pageSetup.orientation != null && pageSetup.orientation !== 'portrait' && pageSetup.orientation !== 'landscape') return false
    if (pageSetup.page_order != null && pageSetup.page_order !== 'downThenOver' && pageSetup.page_order !== 'overThenDown') return false
  }
  const headerFooter = value.header_footer
  if (headerFooter != null) {
    if (!isSpreadsheetProtocolRecord(headerFooter)) return false
    if (
      !isOptionalBoolean(headerFooter.different_odd_even) ||
      !isOptionalBoolean(headerFooter.different_first) ||
      !isOptionalBoolean(headerFooter.scale_with_doc) ||
      !isOptionalBoolean(headerFooter.align_with_margins) ||
      !isNullableStringField(headerFooter.odd_header) ||
      !isNullableStringField(headerFooter.odd_footer) ||
      !isNullableStringField(headerFooter.even_header) ||
      !isNullableStringField(headerFooter.even_footer) ||
      !isNullableStringField(headerFooter.first_header) ||
      !isNullableStringField(headerFooter.first_footer)
    ) return false
  }
  const pageBreaks = value.page_breaks
  if (pageBreaks != null) {
    if (!isSpreadsheetProtocolRecord(pageBreaks)) return false
    if (pageBreaks.rows != null && (!Array.isArray(pageBreaks.rows) || !pageBreaks.rows.every(isSpreadsheetSheetPageBreak))) return false
    if (pageBreaks.columns != null && (!Array.isArray(pageBreaks.columns) || !pageBreaks.columns.every(isSpreadsheetSheetPageBreak))) return false
  }
  return true
}

const isSpreadsheetSheetSnapshot = (value: unknown): value is SpreadsheetSheetSnapshot => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (!/^sheet_[1-9]\d*$/.test(String(value.sheet_id ?? '').trim())) return false
  if (typeof value.name !== 'string' || value.name.trim().length === 0) return false
  if (!isPositiveIntegerNumber(value.row_count) || !isPositiveIntegerNumber(value.col_count)) return false
  if (!isSpreadsheetProtocolRecord(value.chunks)) return false
  if (value.row_metadata != null && !isSpreadsheetSheetAxisMetadataMap(value.row_metadata)) return false
  if (value.column_metadata != null && !isSpreadsheetSheetAxisMetadataMap(value.column_metadata)) return false
  if (value.ooxml_sheet_metadata != null && !isSpreadsheetSheetOoxmlMetadata(value.ooxml_sheet_metadata)) return false
  if (value.tab_color != null && !isSpreadsheetProtocolRecord(value.tab_color)) return false
  if (value.sheet_format_properties != null && !isSpreadsheetSheetFormatProperties(value.sheet_format_properties)) return false
  if (value.calculation_properties != null && !isSpreadsheetSheetCalculationProperties(value.calculation_properties)) return false
  if (value.sheet_views != null && (!Array.isArray(value.sheet_views) || !value.sheet_views.every(isSpreadsheetSheetView))) return false
  if (value.custom_sheet_views != null && (!Array.isArray(value.custom_sheet_views) || !value.custom_sheet_views.every(isSpreadsheetSheetCustomView))) return false
  if (value.print_settings != null && !isSpreadsheetSheetPrintSettings(value.print_settings)) return false
  if (value.ignored_errors != null && (!Array.isArray(value.ignored_errors) || !value.ignored_errors.every(isSpreadsheetSheetIgnoredError))) return false
  if (value.frozen_panes != null) {
    if (!isSpreadsheetProtocolRecord(value.frozen_panes)) return false
    if (!isNonNegativeIntegerNumber(value.frozen_panes.rows) || !isNonNegativeIntegerNumber(value.frozen_panes.cols)) return false
  }
  if (value.hidden != null && typeof value.hidden !== 'boolean') return false
  if (!isNullableRecordField(value.sheet_protection)) return false
  if (value.protected_ranges != null && (!Array.isArray(value.protected_ranges) || !value.protected_ranges.every(isSpreadsheetSheetProtectedRange))) return false
  if (value.auto_filter != null && !isSpreadsheetAutoFilter(value.auto_filter)) return false
  if (value.tables != null && !(
    isSpreadsheetProtocolRecord(value.tables) &&
    Object.entries(value.tables).every(([tableId, table]) => (
      String(tableId ?? '').trim().length > 0 &&
      isSpreadsheetTableDefinition(table)
    ))
  )) return false
  if (value.merge_ranges != null && !(
    isSpreadsheetProtocolRecord(value.merge_ranges) &&
    Object.values(value.merge_ranges).every(isSpreadsheetRangeAddress)
  )) return false
  return true
}

const isSpreadsheetAutoFilterOperator = (value: unknown): value is SpreadsheetAutoFilterOperator => (
  value === 'equals' ||
  value === 'not_equals' ||
  value === 'greater_than' ||
  value === 'greater_than_or_equal' ||
  value === 'less_than' ||
  value === 'less_than_or_equal' ||
  value === 'contains' ||
  value === 'not_contains' ||
  value === 'begins_with' ||
  value === 'ends_with'
)

const isSpreadsheetAutoFilterCondition = (value: unknown): value is SpreadsheetAutoFilterCondition => (
  isSpreadsheetProtocolRecord(value) &&
  isSpreadsheetAutoFilterOperator(value.operator) &&
  typeof value.value === 'string'
)

const isSpreadsheetAutoFilterConditionJoin = (value: unknown): value is SpreadsheetAutoFilterConditionJoin => (
  value === 'and' || value === 'or'
)

const isSpreadsheetAutoFilterColumnCriteria = (value: unknown): value is SpreadsheetAutoFilterColumnCriteria => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isNonNegativeIntegerNumber(value.col) &&
    (value.values === undefined ||
      (Array.isArray(value.values) && value.values.every((entry) => typeof entry === 'string'))) &&
    isOptionalBoolean(value.blank) &&
    (value.conditions === undefined ||
      (Array.isArray(value.conditions) && value.conditions.every(isSpreadsheetAutoFilterCondition))) &&
    (value.condition_join === undefined || isSpreadsheetAutoFilterConditionJoin(value.condition_join))
  )
}

const isSpreadsheetAutoFilterSortCondition = (value: unknown): value is SpreadsheetAutoFilterSortCondition => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isSpreadsheetRangeAddress(value.ref) &&
    isOptionalBoolean(value.descending) &&
    isNullableStringField(value.sort_by) &&
    isNullableStringField(value.custom_list) &&
    isNullableStringField(value.dxf_id) &&
    isNullableStringField(value.icon_set) &&
    isNullableStringField(value.icon_id)
  )
}

const isSpreadsheetAutoFilterSortState = (value: unknown): value is SpreadsheetAutoFilterSortState => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    (value.ref == null || isSpreadsheetRangeAddress(value.ref)) &&
    isOptionalBoolean(value.case_sensitive) &&
    isOptionalBoolean(value.column_sort) &&
    (value.conditions === undefined ||
      (Array.isArray(value.conditions) && value.conditions.every(isSpreadsheetAutoFilterSortCondition)))
  )
}

const isSpreadsheetAutoFilter = (value: unknown): value is SpreadsheetAutoFilter => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isSpreadsheetRangeAddress(value.range) &&
    (value.columns === undefined ||
      (Array.isArray(value.columns) && value.columns.every(isSpreadsheetAutoFilterColumnCriteria))) &&
    (value.sort_state == null || isSpreadsheetAutoFilterSortState(value.sort_state))
  )
}

const isSpreadsheetTableColumnDefinition = (value: unknown): value is SpreadsheetTableColumnDefinition => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isNullableStringField(value.id) &&
    isNullableStringField(value.name) &&
    isNullableStringField(value.totals_row_label) &&
    isNullableStringField(value.totals_row_function) &&
    isNullableStringField(value.totals_row_formula) &&
    isNullableStringField(value.calculated_column_formula)
  )
}

const isSpreadsheetTableDefinition = (value: unknown): value is SpreadsheetTableDefinition => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    String(value.id ?? '').trim().length > 0 &&
    String(value.name ?? '').trim().length > 0 &&
    isSpreadsheetRangeAddress(value.range) &&
    isOptionalBoolean(value.has_headers) &&
    isOptionalBoolean(value.show_filter_buttons) &&
    isOptionalBoolean(value.show_totals) &&
    isNullableStringField(value.style_id) &&
    isOptionalBoolean(value.show_first_column) &&
    isOptionalBoolean(value.show_last_column) &&
    isOptionalBoolean(value.show_row_stripes) &&
    isOptionalBoolean(value.show_column_stripes) &&
    (value.auto_filter == null || isSpreadsheetAutoFilter(value.auto_filter)) &&
    (value.columns === undefined ||
      (Array.isArray(value.columns) && value.columns.every(isSpreadsheetTableColumnDefinition)))
  )
}

const isSpreadsheetTableStyleElement = (value: unknown): value is SpreadsheetTableStyleElement => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    String(value.type ?? '').trim().length > 0 &&
    (value.size == null || isPositiveIntegerNumber(value.size)) &&
    isNullableStringField(value.dxf_id) &&
    (value.style == null || isSpreadsheetProtocolRecord(value.style))
  )
}

const isSpreadsheetTableStyleDefinition = (value: unknown): value is SpreadsheetTableStyleDefinition => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    String(value.name ?? '').trim().length > 0 &&
    isOptionalBoolean(value.is_table) &&
    isOptionalBoolean(value.is_pivot) &&
    (value.elements === undefined ||
      (Array.isArray(value.elements) && value.elements.every(isSpreadsheetTableStyleElement)))
  )
}

const isSpreadsheetValidationRuleKind = (value: unknown): value is SpreadsheetValidationRuleKind => (
  value === 'list' ||
  value === 'wholeNumber' ||
  value === 'decimal' ||
  value === 'date' ||
  value === 'time' ||
  value === 'textLength' ||
  value === 'custom'
)

const isSpreadsheetValidationRuleOperator = (value: unknown): value is SpreadsheetValidationRuleOperator => (
  value === 'between' ||
  value === 'notBetween' ||
  value === 'equal' ||
  value === 'notEqual' ||
  value === 'greaterThan' ||
  value === 'lessThan' ||
  value === 'greaterThanOrEqual' ||
  value === 'lessThanOrEqual'
)

const isSpreadsheetValidationErrorStyle = (value: unknown): value is SpreadsheetValidationErrorStyle => (
  value === 'stop' ||
  value === 'warning' ||
  value === 'information'
)

export const isSpreadsheetValidationRule = (value: unknown): value is SpreadsheetValidationRule => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (String(value.id ?? '').trim().length === 0) return false
  if (!isSpreadsheetValidationRuleKind(value.kind)) return false
  if (value.operator != null && !isSpreadsheetValidationRuleOperator(value.operator)) return false
  if (value.allow_blank != null && typeof value.allow_blank !== 'boolean') return false
  if (value.values != null && (!Array.isArray(value.values) || !value.values.every((entry) => typeof entry === 'string'))) return false
  if (!isNullableStringField(value.formula1)) return false
  if (!isNullableStringField(value.formula2)) return false
  if (!isNullableStringField(value.input_title)) return false
  if (!isNullableStringField(value.input_message)) return false
  if (value.error_style != null && !isSpreadsheetValidationErrorStyle(value.error_style)) return false
  if (!isNullableStringField(value.error_title)) return false
  if (!isNullableStringField(value.error_message)) return false
  return true
}

const isSpreadsheetWorkbookProperties = (value: unknown): value is SpreadsheetWorkbookProperties => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return isOptionalBoolean(value.date_1904) && isNullableStringField(value.code_name)
}

const isSpreadsheetWorkbookProtection = (value: unknown): boolean => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isNullableStringField(value.workbook_password) &&
    isNullableStringField(value.revisions_password) &&
    isOptionalBoolean(value.lock_structure) &&
    isOptionalBoolean(value.lock_windows) &&
    isOptionalBoolean(value.lock_revision) &&
    isNullableStringField(value.algorithm_name) &&
    isNullableStringField(value.hash_value) &&
    isNullableStringField(value.salt_value) &&
    (value.spin_count == null || isNonNegativeIntegerNumber(value.spin_count))
  )
}

const isSpreadsheetWorkbookFileSharing = (value: unknown): boolean => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isOptionalBoolean(value.read_only_recommended) &&
    isNullableStringField(value.user_name) &&
    isNullableStringField(value.reservation_password) &&
    isNullableStringField(value.algorithm_name) &&
    isNullableStringField(value.hash_value) &&
    isNullableStringField(value.salt_value) &&
    (value.spin_count == null || isNonNegativeIntegerNumber(value.spin_count))
  )
}

const isSpreadsheetWorkbookSecurity = (value: unknown): value is SpreadsheetWorkbookSecurity => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    (value.workbook_protection == null || isSpreadsheetWorkbookProtection(value.workbook_protection)) &&
    (value.file_sharing == null || isSpreadsheetWorkbookFileSharing(value.file_sharing))
  )
}

const isSpreadsheetWorkbookDocumentProperties = (value: unknown): value is SpreadsheetWorkbookDocumentProperties => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isNullableStringField(value.title) &&
    isNullableStringField(value.subject) &&
    isNullableStringField(value.creator) &&
    isNullableStringField(value.keywords) &&
    isNullableStringField(value.description) &&
    isNullableStringField(value.last_modified_by) &&
    isNullableStringField(value.revision) &&
    isNullableStringField(value.created) &&
    isNullableStringField(value.modified) &&
    isNullableStringField(value.category) &&
    isNullableStringField(value.content_status) &&
    isNullableStringField(value.application) &&
    isNullableStringField(value.app_version) &&
    isNullableStringField(value.company) &&
    isNullableStringField(value.manager)
  )
}

const isSpreadsheetWorkbookCalculationSettings = (
  value: unknown,
): value is SpreadsheetWorkbookCalculationSettings => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    (value.calc_mode == null ||
      value.calc_mode === 'auto' ||
      value.calc_mode === 'manual' ||
      value.calc_mode === 'autoNoTable') &&
    isOptionalBoolean(value.full_calc_on_load) &&
    isOptionalBoolean(value.force_full_calc) &&
    isOptionalBoolean(value.iterate) &&
    (value.iterate_count == null || isNonNegativeIntegerNumber(value.iterate_count)) &&
    (value.iterate_delta == null || (typeof value.iterate_delta === 'number' && Number.isFinite(value.iterate_delta) && value.iterate_delta >= 0)) &&
    (value.calc_id == null || isNonNegativeIntegerNumber(value.calc_id))
  )
}

const isSpreadsheetWorkbookView = (value: unknown): value is SpreadsheetWorkbookView => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  const optionalInteger = (entry: unknown): boolean => entry == null || (typeof entry === 'number' && Number.isInteger(entry))
  const optionalNonNegativeInteger = (entry: unknown): boolean => entry == null || isNonNegativeIntegerNumber(entry)
  return (
    optionalNonNegativeInteger(value.active_tab) &&
    optionalNonNegativeInteger(value.first_sheet) &&
    (value.visibility == null || value.visibility === 'visible' || value.visibility === 'hidden' || value.visibility === 'veryHidden') &&
    isOptionalBoolean(value.minimized) &&
    isOptionalBoolean(value.show_horizontal_scroll) &&
    isOptionalBoolean(value.show_vertical_scroll) &&
    isOptionalBoolean(value.show_sheet_tabs) &&
    optionalNonNegativeInteger(value.tab_ratio) &&
    optionalInteger(value.x_window) &&
    optionalInteger(value.y_window) &&
    optionalNonNegativeInteger(value.window_width) &&
    optionalNonNegativeInteger(value.window_height)
  )
}

const isSpreadsheetWorkbookThemeColor = (value: unknown): value is SpreadsheetWorkbookThemeColor => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    String(value.name ?? '').trim().length > 0 &&
    isOptionalNonEmptyString(value.rgb) &&
    isOptionalNonEmptyString(value.system_color) &&
    isOptionalNonEmptyString(value.last_color)
  )
}

const isSpreadsheetWorkbookTheme = (value: unknown): value is SpreadsheetWorkbookTheme => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (!isOptionalNonEmptyString(value.name)) return false
  if (value.color_scheme == null) return true
  if (!isSpreadsheetProtocolRecord(value.color_scheme)) return false
  if (!isOptionalNonEmptyString(value.color_scheme.name)) return false
  if (value.color_scheme.colors == null) return true
  return (
    isSpreadsheetProtocolRecord(value.color_scheme.colors) &&
    Object.values(value.color_scheme.colors).every(isSpreadsheetWorkbookThemeColor)
  )
}

const isSpreadsheetWorkbookCustomProperty = (value: unknown): value is SpreadsheetWorkbookCustomProperty => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (String(value.name ?? '').trim().length === 0) return false
  if (value.kind === 'string' || value.kind === 'date') return typeof value.value === 'string'
  if (value.kind === 'boolean') return typeof value.value === 'boolean'
  if (value.kind === 'number') return typeof value.value === 'number' && Number.isFinite(value.value)
  return false
}

export const isSpreadsheetWorkbookSnapshot = (value: unknown): value is SpreadsheetWorkbookSnapshot => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (value.protocol_version !== SPREADSHEET_ENGINE_PROTOCOL_VERSION) return false
  if (String(value.workbook_id ?? '').trim().length === 0) return false
  if (!isNonNegativeIntegerNumber(value.workbook_revision)) return false
  if (value.workbook_properties != null && !isSpreadsheetWorkbookProperties(value.workbook_properties)) return false
  if (value.workbook_security != null && !isSpreadsheetWorkbookSecurity(value.workbook_security)) return false
  if (value.document_properties != null && !isSpreadsheetWorkbookDocumentProperties(value.document_properties)) return false
  if (value.calculation_settings != null && !isSpreadsheetWorkbookCalculationSettings(value.calculation_settings)) return false
  if (value.workbook_views != null && (!Array.isArray(value.workbook_views) || !value.workbook_views.every(isSpreadsheetWorkbookView))) return false
  if (value.workbook_theme != null && !isSpreadsheetWorkbookTheme(value.workbook_theme)) return false
  if (
    value.custom_properties != null &&
    (!isSpreadsheetProtocolRecord(value.custom_properties) ||
      !Object.values(value.custom_properties).every(isSpreadsheetWorkbookCustomProperty))
  ) return false
  if (!Array.isArray(value.sheets) || !value.sheets.every(isSpreadsheetSheetSnapshot)) return false
  const sheetIds = new Set(value.sheets.map((sheet) => sheet.sheet_id.trim()))
  if (sheetIds.size === 0 || sheetIds.size !== value.sheets.length) return false
  if (!Array.isArray(value.sheet_order)) return false
  const orderedIds = value.sheet_order.map((sheetId) => String(sheetId ?? '').trim())
  if (orderedIds.length !== sheetIds.size || orderedIds.some((sheetId) => !sheetIds.has(sheetId))) return false
  if (new Set(orderedIds).size !== orderedIds.length) return false
  if (!isSpreadsheetProtocolRecord(value.named_ranges)) return false
  if (!Object.values(value.named_ranges).every(isSpreadsheetNamedRangeSnapshotValue)) return false
  if (
    value.defined_names != null &&
    (!isSpreadsheetProtocolRecord(value.defined_names) ||
      !Object.values(value.defined_names).every(isSpreadsheetDefinedNameEntry))
  ) return false
  if (!isSpreadsheetProtocolRecord(value.validation_rules)) return false
  if (!Object.values(value.validation_rules).every(isSpreadsheetValidationRule)) return false
  if (!isSpreadsheetRecordOfRecords(value.styles)) return false
  if (
    value.table_styles != null &&
    (!isSpreadsheetProtocolRecord(value.table_styles) ||
      !Object.values(value.table_styles).every(isSpreadsheetTableStyleDefinition))
  ) return false
  if (value.conditional_formats != null && !isSpreadsheetRecordOfRecords(value.conditional_formats)) return false
  if (
    value.sparkline_groups != null &&
    (!isSpreadsheetProtocolRecord(value.sparkline_groups) ||
      !Object.values(value.sparkline_groups).every(isSpreadsheetSparklineGroup))
  ) return false
  if (
    value.derived_artifacts != null &&
    (!isSpreadsheetProtocolRecord(value.derived_artifacts) ||
      !Object.values(value.derived_artifacts).every(isSpreadsheetDerivedArtifact))
  ) return false
  if (!isSpreadsheetRecordOfRecords(value.external_sources)) return false
  if (!isSpreadsheetExcelArtifactManifest(value.ooxml_attachments)) return false
  return true
}

export const isSpreadsheetApplyOpsPayload = (value: unknown): value is SpreadsheetApplyOpsPayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    Array.isArray(value.ops) &&
    value.ops.every(isSpreadsheetEngineOperationEnvelope) &&
    isOptionalBoolean(value.allowQueryTabEdit) &&
    isOptionalSpreadsheetStyleSidecars(value.styles) &&
    isOptionalBoolean(value.returnAcceptedOperations)
  )
}

export const isSpreadsheetGetRangePayload = (value: unknown): value is SpreadsheetGetRangePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return isSpreadsheetWorkerRangeAddress(value.range) && (value.mode === 'raw' || value.mode === 'computed')
}

export const isSpreadsheetEvaluateDraftFormulaPayload = (value: unknown): value is SpreadsheetEvaluateDraftFormulaPayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    typeof value.formula === 'string' &&
    value.formula.trim().length > 0 &&
    (value.sheet_id == null || (typeof value.sheet_id === 'string' && value.sheet_id.trim().length > 0))
  )
}

export const isSpreadsheetCopyRangePayload = (value: unknown): value is SpreadsheetCopyRangePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return isSpreadsheetWorkerRangeAddress(value.range)
}

export const isSpreadsheetCopyRangeToPayload = (value: unknown): value is SpreadsheetCopyRangeToPayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isOptionalNonEmptyString(value.sheet_id) &&
    isSpreadsheetWorkerRangeAddress(value.source_range) &&
    isNonNegativeIntegerNumber(value.target_start_row) &&
    isNonNegativeIntegerNumber(value.target_start_col) &&
    isOptionalNonEmptyString(value.clear_op_id) &&
    isOptionalNonEmptyString(value.set_op_id) &&
    isOptionalNonEmptyString(value.actor_client_id) &&
    isOptionalFiniteNumber(value.created_at_ms) &&
    isOptionalBoolean(value.validation_override) &&
    isOptionalBoolean(value.allowQueryTabEdit)
  )
}

export const isSpreadsheetCopyRangeFormatsToPayload = (value: unknown): value is SpreadsheetCopyRangeFormatsToPayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isOptionalNonEmptyString(value.sheet_id) &&
    isSpreadsheetWorkerRangeAddress(value.source_range) &&
    isSpreadsheetWorkerRangeAddress(value.target_range) &&
    isOptionalNonEmptyString(value.op_id) &&
    isOptionalNonEmptyString(value.actor_client_id) &&
    isOptionalFiniteNumber(value.created_at_ms) &&
    isOptionalBoolean(value.allowQueryTabEdit)
  )
}

export const isSpreadsheetPasteTsvPayload = (value: unknown): value is SpreadsheetPasteTsvPayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isOptionalNonEmptyString(value.sheet_id) &&
    isNonNegativeIntegerNumber(value.start_row) &&
    isNonNegativeIntegerNumber(value.start_col) &&
    typeof value.tsv === 'string' &&
    isOptionalNonEmptyString(value.op_id) &&
    isOptionalNonEmptyString(value.actor_client_id) &&
    isOptionalFiniteNumber(value.created_at_ms) &&
    isOptionalBoolean(value.validation_override) &&
    isOptionalBoolean(value.allowQueryTabEdit)
  )
}

export const isSpreadsheetFillRangePayload = (value: unknown): value is SpreadsheetFillRangePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isOptionalNonEmptyString(value.sheet_id) &&
    isSpreadsheetWorkerRangeAddress(value.source_range) &&
    isSpreadsheetWorkerRangeAddress(value.target_range) &&
    (value.mode === undefined ||
      value.mode === 'auto' ||
      value.mode === 'copyCells' ||
      value.mode === 'fillSeries' ||
      value.mode === 'fillDays' ||
      value.mode === 'fillWeekdays' ||
      value.mode === 'fillMonths' ||
      value.mode === 'fillYears') &&
    isOptionalNonEmptyString(value.op_id) &&
    isOptionalNonEmptyString(value.actor_client_id) &&
    isOptionalFiniteNumber(value.created_at_ms) &&
    isOptionalBoolean(value.validation_override) &&
    isOptionalBoolean(value.allowQueryTabEdit)
  )
}

export const isSpreadsheetFindReplacePayload = (value: unknown): value is SpreadsheetFindReplacePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isOptionalNonEmptyString(value.sheet_id) &&
    isSpreadsheetWorkerRangeAddress(value.range) &&
    typeof value.query === 'string' &&
    value.query.length > 0 &&
    typeof value.replacement === 'string' &&
    isOptionalBoolean(value.match_case) &&
    isOptionalBoolean(value.whole_cell) &&
    isOptionalBoolean(value.validation_override) &&
    isOptionalNonEmptyString(value.op_id) &&
    isOptionalNonEmptyString(value.actor_client_id) &&
    isOptionalFiniteNumber(value.created_at_ms) &&
    isOptionalBoolean(value.allowQueryTabEdit)
  )
}

export const isSpreadsheetRemoveDuplicatesPayload = (value: unknown): value is SpreadsheetRemoveDuplicatesPayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isOptionalNonEmptyString(value.sheet_id) &&
    isSpreadsheetWorkerRangeAddress(value.range) &&
    (value.key_columns === undefined ||
      (Array.isArray(value.key_columns) && value.key_columns.every(isNonNegativeIntegerNumber))) &&
    isOptionalBoolean(value.has_header) &&
    isOptionalBoolean(value.validation_override) &&
    isOptionalNonEmptyString(value.op_id) &&
    isOptionalNonEmptyString(value.actor_client_id) &&
    isOptionalFiniteNumber(value.created_at_ms) &&
    isOptionalBoolean(value.allowQueryTabEdit)
  )
}

export const isSpreadsheetSortRangePayload = (value: unknown): value is SpreadsheetSortRangePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isOptionalNonEmptyString(value.sheet_id) &&
    isSpreadsheetWorkerRangeAddress(value.range) &&
    (value.sort_columns === undefined ||
      (Array.isArray(value.sort_columns) && value.sort_columns.every((column) => (
        isSpreadsheetProtocolRecord(column) &&
        isNonNegativeIntegerNumber(column.col) &&
        isOptionalBoolean(column.descending)
      )))) &&
    isOptionalBoolean(value.has_header) &&
    isOptionalBoolean(value.validation_override) &&
    isOptionalNonEmptyString(value.clear_op_id) &&
    isOptionalNonEmptyString(value.set_op_id) &&
    isOptionalNonEmptyString(value.actor_client_id) &&
    isOptionalFiniteNumber(value.created_at_ms) &&
    isOptionalBoolean(value.allowQueryTabEdit)
  )
}

export const isSpreadsheetMoveRangePayload = (value: unknown): value is SpreadsheetMoveRangePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isOptionalNonEmptyString(value.sheet_id) &&
    isSpreadsheetWorkerRangeAddress(value.source_range) &&
    isNonNegativeIntegerNumber(value.target_start_row) &&
    isNonNegativeIntegerNumber(value.target_start_col) &&
    isOptionalNonEmptyString(value.clear_op_id) &&
    isOptionalNonEmptyString(value.set_op_id) &&
    isOptionalNonEmptyString(value.actor_client_id) &&
    isOptionalFiniteNumber(value.created_at_ms) &&
    isOptionalBoolean(value.validation_override) &&
    isOptionalBoolean(value.allowQueryTabEdit)
  )
}

export const isSpreadsheetRecalcIncrementalPayload = (value: unknown): value is SpreadsheetRecalcIncrementalPayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isPositiveIntegerNumber(value.maxFormulaCount) &&
    typeof value.schedulerToken === 'string' &&
    value.schedulerToken.trim().length > 0
  )
}

const isNonNegativeFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0

export const isSpreadsheetGetViewportPayload = (value: unknown): value is SpreadsheetGetViewportPayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isNonNegativeFiniteNumber(value.x) &&
    isNonNegativeFiniteNumber(value.y) &&
    isNonNegativeFiniteNumber(value.width) &&
    isNonNegativeFiniteNumber(value.height) &&
    isNonNegativeIntegerNumber(value.seq) &&
    isOptionalBoolean(value.forceSpreadsheet) &&
    (value.gridCols == null || isPositiveIntegerNumber(value.gridCols)) &&
    (value.gridRows == null || isPositiveIntegerNumber(value.gridRows)) &&
    (value.frozenRows == null || isNonNegativeIntegerNumber(value.frozenRows)) &&
    (value.frozenCols == null || isNonNegativeIntegerNumber(value.frozenCols)) &&
    (
      value.sheetId == null ||
      (typeof value.sheetId === 'number' && Number.isInteger(value.sheetId) && value.sheetId > 0)
    )
  )
}

export const isSpreadsheetPresenceUpdatePayload = (value: unknown): value is SpreadsheetPresenceUpdatePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  const buffer = value.sharedStateBuffer
  return (
    value.kind === 'layout_shared_state' &&
    (
      buffer instanceof ArrayBuffer ||
      (typeof SharedArrayBuffer !== 'undefined' && buffer instanceof SharedArrayBuffer)
    )
  )
}

export const isSpreadsheetCellValue = (value: unknown): value is SpreadsheetCellValue => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  switch (value.kind) {
    case 'blank':
      return true
    case 'number':
      return typeof value.value === 'number' && Number.isFinite(value.value)
    case 'text':
      return typeof value.value === 'string'
    case 'boolean':
      return typeof value.value === 'boolean'
    case 'datetime':
      return typeof value.epoch_ms === 'number' && Number.isFinite(value.epoch_ms)
    case 'error':
      return (
        typeof value.code === 'string' &&
        (SPREADSHEET_ENGINE_ERROR_CODES as readonly string[]).includes(value.code) &&
        (value.message == null || typeof value.message === 'string')
      )
    case 'pending_external':
      return (
        typeof value.request_id === 'string' &&
        value.request_id.trim().length > 0 &&
        typeof value.source_id === 'string' &&
        value.source_id.trim().length > 0
      )
    case 'dynamic_array':
      return Array.isArray(value.rows) && value.rows.every((row) => (
        Array.isArray(row) && row.every(isSpreadsheetCellValue)
      ))
    default:
      return false
  }
}

const isNullableStringField = (value: unknown): boolean =>
  value == null || typeof value === 'string'

const isNullableRecordField = (value: unknown): boolean =>
  value == null || isSpreadsheetProtocolRecord(value)

const isNonNegativeIntegerNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0

const isPositiveIntegerNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0

const isSpreadsheetFormulaParseError = (value: unknown): value is SpreadsheetFormulaParseError => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return isNonNegativeIntegerNumber(value.offset) && typeof value.message === 'string'
}

const isSpreadsheetFormulaAuthoringCellCoord = (
  value: unknown,
): value is SpreadsheetFormulaAuthoringCellCoord => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return isNonNegativeIntegerNumber(value.row) && isNonNegativeIntegerNumber(value.col)
}

const isSpreadsheetFormulaAuthoringRange = (
  value: unknown,
): value is SpreadsheetFormulaAuthoringRange => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    (value.sheet_name == null || typeof value.sheet_name === 'string') &&
    isSpreadsheetFormulaAuthoringCellCoord(value.start) &&
    isSpreadsheetFormulaAuthoringCellCoord(value.end)
  )
}

const isSpreadsheetFormulaAuthoringSpan = (
  value: unknown,
): value is SpreadsheetFormulaAuthoringSpan => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (!isNonNegativeIntegerNumber(value.start_offset) || !isNonNegativeIntegerNumber(value.end_offset)) return false
  if (value.end_offset < value.start_offset) return false
  switch (value.kind) {
    case 'reference':
      return (
        (
          value.reference_kind === 'cell' ||
          value.reference_kind === 'range' ||
          value.reference_kind === 'whole_column' ||
          value.reference_kind === 'whole_row' ||
          value.reference_kind === 'spill'
        ) &&
        typeof value.text === 'string' &&
        (value.sheet_name == null || typeof value.sheet_name === 'string') &&
        isSpreadsheetFormulaAuthoringRange(value.range)
      )
    case 'function':
      return (
        typeof value.name === 'string' &&
        value.name.trim().length > 0 &&
        isNonNegativeIntegerNumber(value.open_paren_offset)
      )
    case 'named_reference':
      return typeof value.name === 'string' && value.name.trim().length > 0
    default:
      return false
  }
}

export const isSpreadsheetFormulaAuthoringAnalysis = (
  value: unknown,
): value is SpreadsheetFormulaAuthoringAnalysis => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    Array.isArray(value.spans) &&
    value.spans.every(isSpreadsheetFormulaAuthoringSpan) &&
    (value.parse_error == null || isSpreadsheetFormulaParseError(value.parse_error))
  )
}

const isSpreadsheetRangeCell = (value: unknown): value is SpreadsheetRangeCell => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isNonNegativeIntegerNumber(value.row) &&
    isNonNegativeIntegerNumber(value.col) &&
    typeof value.raw_input === 'string' &&
    isSpreadsheetCellValue(value.value) &&
    (value.display_value == null || typeof value.display_value === 'string') &&
    isNullableStringField(value.style_id) &&
    isNullableStringField(value.validation_id) &&
    isNullableStringField(value.comment) &&
    isNullableStringField(value.hyperlink) &&
    isNullableRecordField(value.protection) &&
    isNullableRecordField(value.metadata) &&
    Number.isFinite(Number(value.revision))
  )
}

export const isSpreadsheetRangeSnapshot = (value: unknown): value is SpreadsheetRangeSnapshot => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    String(value.workbook_id ?? '').trim().length > 0 &&
    Number.isFinite(Number(value.workbook_revision)) &&
    String(value.sheet_id ?? '').trim().length > 0 &&
    isSpreadsheetRangeAddress(value.range) &&
    (value.mode === 'raw' || value.mode === 'computed') &&
    Array.isArray(value.rows) &&
    value.rows.every((row) => Array.isArray(row) && row.every(isSpreadsheetRangeCell))
  )
}

const isSpreadsheetClipboardCell = (value: unknown): value is SpreadsheetClipboardCell => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    Number.isFinite(Number(value.row_offset)) &&
    Number.isFinite(Number(value.col_offset)) &&
    typeof value.raw_input === 'string' &&
    isSpreadsheetCellValue(value.computed_value) &&
    isNullableStringField(value.display_value) &&
    isNullableStringField(value.style_id) &&
    isNullableRecordField(value.style) &&
    isNullableStringField(value.validation_id) &&
    isNullableStringField(value.comment) &&
    isNullableStringField(value.hyperlink) &&
    isNullableRecordField(value.protection) &&
    isNullableRecordField(value.metadata)
  )
}

const isSpreadsheetClipboardPayload = (value: unknown): value is SpreadsheetClipboardPayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    String(value.workbook_id ?? '').trim().length > 0 &&
    String(value.sheet_id ?? '').trim().length > 0 &&
    isSpreadsheetRangeAddress(value.range) &&
    isNonNegativeIntegerNumber(value.row_count) &&
    isNonNegativeIntegerNumber(value.col_count) &&
    Array.isArray(value.cells) &&
    value.cells.every((row) => Array.isArray(row) && row.every(isSpreadsheetClipboardCell)) &&
    typeof value.tsv === 'string'
  )
}

export const isSpreadsheetPasteSpecialPayload = (value: unknown): value is SpreadsheetPasteSpecialPayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isOptionalNonEmptyString(value.sheet_id) &&
    isNonNegativeIntegerNumber(value.start_row) &&
    isNonNegativeIntegerNumber(value.start_col) &&
    isSpreadsheetClipboardPayload(value.clipboard) &&
    (value.mode === 'values' || value.mode === 'formulas' || value.mode === 'formats' || value.mode === 'transpose') &&
    isOptionalNonEmptyString(value.op_id) &&
    isOptionalNonEmptyString(value.actor_client_id) &&
    isOptionalFiniteNumber(value.created_at_ms) &&
    isOptionalBoolean(value.allowQueryTabEdit)
  )
}

export const getSpreadsheetRoutedResponseRequestId = (value: unknown): string | null => {
  if (!isSpreadsheetProtocolRecord(value)) return null
  if (typeof value.requestId !== 'string') return null
  const requestId = value.requestId.trim()
  return requestId.length > 0 ? requestId : null
}

const hasValidOptionalRoutedResponseMetadata = (value: Record<string, unknown>): boolean => {
  if (value.requestId != null && getSpreadsheetRoutedResponseRequestId(value) === null) return false
  if (value.workbookId != null && (typeof value.workbookId !== 'string' || value.workbookId.trim().length === 0)) {
    return false
  }
  if (value.sheetId != null && (typeof value.sheetId !== 'string' || value.sheetId.trim().length === 0)) {
    return false
  }
  if (value.workbookRevision != null && !Number.isFinite(Number(value.workbookRevision))) return false
  return true
}

export const isSpreadsheetExternalFunctionRequest = (value: unknown): value is SpreadsheetExternalFunctionRequest => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  const args = Array.isArray(value.arguments) ? value.arguments : []
  return (
    String(value.request_id ?? '').trim().length > 0 &&
    String(value.workbook_id ?? '').trim().length > 0 &&
    String(value.sheet_id ?? '').trim().length > 0 &&
    isSpreadsheetCellAddress(value.address) &&
    String(value.source_id ?? '').trim().length > 0 &&
    value.function_name === 'SQL' &&
    args.length > 0 &&
    args.every((arg) => {
      if (!isSpreadsheetProtocolRecord(arg)) return false
      if (arg.kind === 'query') return String(arg.sql ?? '').trim().length > 0
      if (arg.kind === 'range') {
        return isSpreadsheetRangeAddress(arg.range) && (arg.mode === 'raw' || arg.mode === 'computed')
      }
      return false
    }) &&
    Number.isFinite(Number(value.created_at_ms)) &&
    Number.isFinite(Number(value.timeout_ms)) &&
    Number.isFinite(Number(value.max_rows)) &&
    Number.isFinite(Number(value.max_cols)) &&
    Number.isFinite(Number(value.max_bytes))
  )
}

export const isSpreadsheetExternalRequestedResponsePayload = (
  value: unknown,
): value is SpreadsheetExternalRequestedResponsePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (!hasValidOptionalRoutedResponseMetadata(value)) return false
  return isSpreadsheetExternalFunctionRequest(value.request)
}

export const isSpreadsheetExternalFunctionResult = (value: unknown): value is SpreadsheetExternalFunctionResult => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (
    String(value.request_id ?? '').trim().length === 0 ||
    String(value.workbook_id ?? '').trim().length === 0 ||
    String(value.sheet_id ?? '').trim().length === 0 ||
    !isSpreadsheetCellAddress(value.address) ||
    !Number.isFinite(Number(value.completed_at_ms))
  ) {
    return false
  }
  if (value.status === 'ok') {
    return Array.isArray(value.values) && value.values.every((row) => (
      Array.isArray(row) && row.every(isSpreadsheetCellValue)
    ))
  }
  if (value.status === 'error') {
    if (!isSpreadsheetProtocolRecord(value.error)) return false
    return (
      typeof value.error.code === 'string' &&
      (SPREADSHEET_ENGINE_ERROR_CODES as readonly string[]).includes(value.error.code) &&
      typeof value.error.message === 'string' &&
      typeof value.error.category === 'string'
    )
  }
  return value.status === 'cancelled'
}

const isSpreadsheetExcelWorkbookIoFormat = (value: unknown): value is SpreadsheetExcelWorkbookIoFormat =>
  value === 'xlsx' || value === 'xlsm'

const isSpreadsheetExcelUnsupportedArtifactPolicy = (value: unknown): value is SpreadsheetExcelUnsupportedArtifactPolicy =>
  value === undefined ||
  value === 'preserve_non_authoritative' ||
  value === 'drop_with_warning' ||
  value === 'reject'

const isSpreadsheetExcelImportOptions = (value: unknown): value is SpreadsheetExcelImportOptions => {
  if (value == null) return true
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isSpreadsheetExcelUnsupportedArtifactPolicy(value.unsupported_artifact_policy) &&
    isOptionalBoolean(value.include_preview_values)
  )
}

export const isSpreadsheetExcelImportPreviewPayload = (value: unknown): value is SpreadsheetExcelImportPreviewPayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isSpreadsheetExcelWorkbookIoFormat(value.format) &&
    typeof value.file_name === 'string' &&
    value.file_name.trim().length > 0 &&
    isOptionalNonEmptyString(value.content_type) &&
    typeof value.package_base64 === 'string' &&
    value.package_base64.trim().length > 0 &&
    (value.byte_length == null || isNonNegativeIntegerNumber(value.byte_length)) &&
    isSpreadsheetExcelImportOptions(value.options)
  )
}

export const isSpreadsheetExcelImportWorkbookPayload = (value: unknown): value is SpreadsheetExcelImportWorkbookPayload => {
  if (!isSpreadsheetExcelImportPreviewPayload(value)) return false
  const payload = value as SpreadsheetExcelImportWorkbookPayload
  return (
    (payload.import_mode === undefined || payload.import_mode === 'new_workbook' || payload.import_mode === 'replace_workbook') &&
    isOptionalNonEmptyString(payload.target_workbook_id) &&
    isOptionalNonEmptyString(payload.actor_client_id) &&
    isOptionalFiniteNumber(payload.created_at_ms)
  )
}

export const isSpreadsheetExcelExportWorkbookPayload = (value: unknown): value is SpreadsheetExcelExportWorkbookPayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isSpreadsheetExcelWorkbookIoFormat(value.format) &&
    isOptionalNonEmptyString(value.file_name) &&
    isOptionalBoolean(value.include_calculated_values) &&
    isOptionalBoolean(value.preserve_non_authoritative_artifacts) &&
    isOptionalNonEmptyString(value.actor_client_id) &&
    isOptionalFiniteNumber(value.created_at_ms)
  )
}

export const isSpreadsheetExcelExportSnapshotPayload = (value: unknown): value is SpreadsheetExcelExportSnapshotPayload => {
  if (!isSpreadsheetExcelExportWorkbookPayload(value)) return false
  return isSpreadsheetWorkbookSnapshot((value as SpreadsheetExcelExportSnapshotPayload).snapshot)
}

export const isSpreadsheetRestoreWorkbookSnapshotPayload = (
  value: unknown,
): value is SpreadsheetRestoreWorkbookSnapshotPayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isSpreadsheetWorkbookSnapshot(value.snapshot) &&
    isOptionalNonEmptyString(value.actor_client_id) &&
    isOptionalFiniteNumber(value.created_at_ms)
  )
}

export const isSpreadsheetExcelRoundTripValidationPayload = (
  value: unknown,
): value is SpreadsheetExcelRoundTripValidationPayload =>
  isSpreadsheetExcelImportPreviewPayload(value)

const isSpreadsheetExcelIoWarning = (value: unknown): value is SpreadsheetExcelIoWarning => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    typeof value.code === 'string' &&
    value.code.trim().length > 0 &&
    (value.severity === 'info' || value.severity === 'warning' || value.severity === 'error') &&
    typeof value.message === 'string' &&
    value.message.trim().length > 0 &&
    isOptionalNonEmptyString(value.sheet_name) &&
    isOptionalNonEmptyString(value.part_name)
  )
}

const isSpreadsheetExcelOoxmlArtifactManifestEntry = (
  value: unknown,
): value is SpreadsheetExcelOoxmlArtifactManifestEntry => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  const hasValidRelationships = value.relationships == null || (
    Array.isArray(value.relationships) &&
    value.relationships.every((relationship) => (
      isSpreadsheetProtocolRecord(relationship) &&
      typeof relationship.id === 'string' &&
      relationship.id.trim().length > 0 &&
      typeof relationship.type === 'string' &&
      relationship.type.trim().length > 0 &&
      typeof relationship.target === 'string' &&
      relationship.target.trim().length > 0 &&
      isOptionalNonEmptyString(relationship.target_mode)
    ))
  )
  return (
    typeof value.part_name === 'string' &&
    value.part_name.trim().length > 0 &&
    isOptionalNonEmptyString(value.content_type) &&
    isOptionalNonEmptyString(value.relationship_type) &&
    isOptionalRelationshipSourcePartName(value.source_part_name) &&
    isOptionalNonEmptyString(value.relationship_id) &&
    isOptionalNonEmptyString(value.relationship_target) &&
    isOptionalNonEmptyString(value.relationship_target_mode) &&
    (value.marker_attributes == null || isRecordOfStrings(value.marker_attributes)) &&
    (value.byte_length == null || isNonNegativeIntegerNumber(value.byte_length)) &&
    isOptionalNonEmptyString(value.checksum_crc32) &&
    isOptionalNonEmptyString(value.payload_base64) &&
    hasValidRelationships &&
    (
      value.preservation === 'modeled_engine_state' ||
      value.preservation === 'non_authoritative_attachment' ||
      value.preservation === 'dropped'
    )
  )
}

const isSpreadsheetExcelImportPreviewSheet = (value: unknown): value is SpreadsheetExcelImportPreviewSheet => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    typeof value.name === 'string' &&
    value.name.trim().length > 0 &&
    isNonNegativeIntegerNumber(value.row_count) &&
    isNonNegativeIntegerNumber(value.col_count) &&
    isOptionalBoolean(value.hidden) &&
    (value.table_count == null || isNonNegativeIntegerNumber(value.table_count)) &&
    (value.formula_count == null || isNonNegativeIntegerNumber(value.formula_count)) &&
    (value.warning_count == null || isNonNegativeIntegerNumber(value.warning_count))
  )
}

const isSpreadsheetExcelArtifactManifest = (value: unknown): value is SpreadsheetExcelOoxmlArtifactManifestEntry[] =>
  value === undefined ||
  (Array.isArray(value) && value.every(isSpreadsheetExcelOoxmlArtifactManifestEntry))

export const isSpreadsheetExcelImportPreviewResponsePayload = (
  value: unknown,
): value is SpreadsheetExcelImportPreviewResponsePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    hasValidOptionalRoutedResponseMetadata(value) &&
    isSpreadsheetExcelWorkbookIoFormat(value.format) &&
    typeof value.file_name === 'string' &&
    value.file_name.trim().length > 0 &&
    isOptionalNonEmptyString(value.workbook_name) &&
    Array.isArray(value.sheets) &&
    value.sheets.every(isSpreadsheetExcelImportPreviewSheet) &&
    (value.defined_name_count == null || isNonNegativeIntegerNumber(value.defined_name_count)) &&
    (value.style_count == null || isNonNegativeIntegerNumber(value.style_count)) &&
    Array.isArray(value.warnings) &&
    value.warnings.every(isSpreadsheetExcelIoWarning) &&
    isSpreadsheetExcelArtifactManifest(value.advanced_ooxml_artifacts)
  )
}

export const isSpreadsheetExcelImportResponsePayload = (
  value: unknown,
): value is SpreadsheetExcelImportResponsePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  const hasSnapshot = value.snapshot != null
  const engineOps = value.engine_ops
  const hasEngineOps = Array.isArray(engineOps)
  return (
    hasValidOptionalRoutedResponseMetadata(value) &&
    typeof value.workbook_id === 'string' &&
    value.workbook_id.trim().length > 0 &&
    isNonNegativeIntegerNumber(value.workbook_revision) &&
    (!hasSnapshot || isSpreadsheetWorkbookSnapshot(value.snapshot)) &&
    (!hasEngineOps || engineOps.every(isSpreadsheetEngineOperationEnvelope)) &&
    (hasSnapshot || hasEngineOps) &&
    Array.isArray(value.warnings) &&
    value.warnings.every(isSpreadsheetExcelIoWarning) &&
    isSpreadsheetExcelArtifactManifest(value.advanced_ooxml_artifacts)
  )
}

export const isSpreadsheetExcelExportResponsePayload = (
  value: unknown,
): value is SpreadsheetExcelExportResponsePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    hasValidOptionalRoutedResponseMetadata(value) &&
    isSpreadsheetExcelWorkbookIoFormat(value.format) &&
    typeof value.file_name === 'string' &&
    value.file_name.trim().length > 0 &&
    typeof value.content_type === 'string' &&
    value.content_type.trim().length > 0 &&
    typeof value.package_base64 === 'string' &&
    value.package_base64.trim().length > 0 &&
    isNonNegativeIntegerNumber(value.byte_length) &&
    Array.isArray(value.warnings) &&
    value.warnings.every(isSpreadsheetExcelIoWarning) &&
    isSpreadsheetExcelArtifactManifest(value.advanced_ooxml_artifacts)
  )
}

export const isSpreadsheetRestoreWorkbookSnapshotResponsePayload = (
  value: unknown,
): value is SpreadsheetRestoreWorkbookSnapshotResponsePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    hasValidOptionalRoutedResponseMetadata(value) &&
    typeof value.workbook_id === 'string' &&
    value.workbook_id.trim().length > 0 &&
    isNonNegativeIntegerNumber(value.workbook_revision) &&
    isNonNegativeIntegerNumber(value.sheet_count)
  )
}

export const isSpreadsheetWorkbookSnapshotResponsePayload = (
  value: unknown,
): value is SpreadsheetWorkbookSnapshotResponsePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    hasValidOptionalRoutedResponseMetadata(value) &&
    typeof value.workbook_id === 'string' &&
    value.workbook_id.trim().length > 0 &&
    isNonNegativeIntegerNumber(value.workbook_revision) &&
    isSpreadsheetWorkbookSnapshot(value.snapshot)
  )
}

export const isSpreadsheetExcelRoundTripValidationResponsePayload = (
  value: unknown,
): value is SpreadsheetExcelRoundTripValidationResponsePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    hasValidOptionalRoutedResponseMetadata(value) &&
    typeof value.ok === 'boolean' &&
    Array.isArray(value.semantic_diffs) &&
    value.semantic_diffs.every((diff) => typeof diff === 'string') &&
    Array.isArray(value.import_warnings) &&
    value.import_warnings.every(isSpreadsheetExcelIoWarning) &&
    Array.isArray(value.export_warnings) &&
    value.export_warnings.every(isSpreadsheetExcelIoWarning) &&
    isSpreadsheetExcelArtifactManifest(value.advanced_ooxml_artifacts)
  )
}

const isSpreadsheetEngineDiagnostic = (value: unknown): value is SpreadsheetEngineDiagnostic => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    String(value.workbook_id ?? '').trim().length > 0 &&
    (value.sheet_id == null || typeof value.sheet_id === 'string') &&
    (value.request_id == null || typeof value.request_id === 'string') &&
    (value.revision == null || Number.isFinite(Number(value.revision))) &&
    (value.chunk_id == null || typeof value.chunk_id === 'string') &&
    (value.operation_type == null || (typeof value.operation_type === 'string' && isSpreadsheetOperationType(value.operation_type))) &&
    (
      value.category === 'engine' ||
      value.category === 'worker' ||
      value.category === 'document_session' ||
      value.category === 'external_function'
    ) &&
    typeof value.message === 'string' &&
    (value.duration_ms == null || Number.isFinite(Number(value.duration_ms)))
  )
}

const isSpreadsheetEngineDelta = (value: unknown): value is SpreadsheetEngineDelta => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    String(value.workbook_id ?? '').trim().length > 0 &&
    Number.isFinite(Number(value.workbook_revision)) &&
    Array.isArray(value.changed_cells) &&
    value.changed_cells.every(isSpreadsheetCellAddress) &&
    Array.isArray(value.changed_ranges) &&
    value.changed_ranges.every(isSpreadsheetRangeAddress) &&
    Array.isArray(value.invalidated_chunk_ids) &&
    value.invalidated_chunk_ids.every((chunkId) => typeof chunkId === 'string' && chunkId.trim().length > 0) &&
    (
      value.recalc_status === 'idle' ||
      value.recalc_status === 'dirty' ||
      value.recalc_status === 'calculating' ||
      value.recalc_status === 'blocked'
    ) &&
    Array.isArray(value.spill_changes) &&
    value.spill_changes.every(isSpreadsheetRangeAddress) &&
    Array.isArray(value.external_changes) &&
    value.external_changes.every(isSpreadsheetExternalFunctionRequest) &&
    Array.isArray(value.diagnostics) &&
    value.diagnostics.every(isSpreadsheetEngineDiagnostic) &&
    Array.isArray(value.ui_invalidations) &&
    value.ui_invalidations.every((invalidation) => (
      invalidation === 'viewport' ||
      invalidation === 'formula_bar' ||
      invalidation === 'sheet_tabs' ||
      invalidation === 'format_toolbar' ||
      invalidation === 'presence'
    ))
  )
}

const isSpreadsheetRecalcStepMetadata = (value: unknown): value is SpreadsheetRecalcStepMetadata => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    Array.isArray(value.evaluated_cells) &&
    value.evaluated_cells.every(isSpreadsheetCellAddress) &&
    Number.isFinite(Number(value.remaining_formula_count)) &&
    typeof value.completed === 'boolean' &&
    String(value.scheduler_token ?? '').trim().length > 0
  )
}

const isSpreadsheetViewportCell = (value: unknown): value is SpreadsheetViewportCell => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (!Number.isFinite(Number(value.col)) || !Number.isFinite(Number(value.row))) return false
  if (!isSpreadsheetProtocolRecord(value.data)) return false
  return (
    typeof value.data.value === 'string' &&
    (value.data.formula == null || typeof value.data.formula === 'string') &&
    (value.data.format == null || isSpreadsheetProtocolRecord(value.data.format)) &&
    isNullableStringField(value.data.validation_id) &&
    isNullableStringField(value.data.comment) &&
    isNullableStringField(value.data.hyperlink) &&
    isNullableRecordField(value.data.protection) &&
    isNullableRecordField(value.data.metadata)
  )
}

export const isSpreadsheetViewportResponsePayload = (value: unknown): value is SpreadsheetViewportResponsePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (!hasValidOptionalRoutedResponseMetadata(value)) return false
  if (!Array.isArray(value.data) || !value.data.every(isSpreadsheetViewportCell)) return false
  if (value.seq != null && !Number.isFinite(Number(value.seq))) return false
  if (value.startCol != null && !Number.isFinite(Number(value.startCol))) return false
  if (value.endCol != null && !Number.isFinite(Number(value.endCol))) return false
  if (value.startRow != null && !Number.isFinite(Number(value.startRow))) return false
  if (value.endRow != null && !Number.isFinite(Number(value.endRow))) return false
  if (value.source != null && typeof value.source !== 'string') return false
  if (value.tab_id != null && typeof value.tab_id !== 'string') return false
  if (value.sheet_id != null && !Number.isFinite(Number(value.sheet_id))) return false
  return true
}

export const isSpreadsheetRangeResponsePayload = (value: unknown): value is SpreadsheetRangeResponsePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (!hasValidOptionalRoutedResponseMetadata(value)) return false
  return isSpreadsheetRangeSnapshot(value.range)
}

export const isSpreadsheetCopyPayloadResponsePayload = (value: unknown): value is SpreadsheetCopyPayloadResponsePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (!hasValidOptionalRoutedResponseMetadata(value)) return false
  return isSpreadsheetClipboardPayload(value.clipboard)
}

const isSpreadsheetSheetListEntry = (value: unknown): value is SpreadsheetSheetListEntry => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  const id = Number(value.id)
  if (!Number.isFinite(id) || id <= 0) return false
  if (typeof value.name !== 'string') return false
  if (value.row_count != null && (!Number.isFinite(Number(value.row_count)) || Number(value.row_count) <= 0)) return false
  if (value.col_count != null && (!Number.isFinite(Number(value.col_count)) || Number(value.col_count) <= 0)) return false
  if (value.row_metadata != null && !isSpreadsheetProtocolRecord(value.row_metadata)) return false
  if (value.column_metadata != null && !isSpreadsheetProtocolRecord(value.column_metadata)) return false
  if (value.frozen_panes != null) {
    if (!isSpreadsheetProtocolRecord(value.frozen_panes)) return false
    if (!isNonNegativeIntegerNumber(value.frozen_panes.rows) || !isNonNegativeIntegerNumber(value.frozen_panes.cols)) return false
  }
  if (value.hidden != null && typeof value.hidden !== 'boolean') return false
  if (!isNullableRecordField(value.sheet_protection)) return false
  if (value.auto_filter != null && !isSpreadsheetAutoFilter(value.auto_filter)) return false
  if (value.tables != null && !(
    isSpreadsheetProtocolRecord(value.tables) &&
    Object.entries(value.tables).every(([tableId, table]) => (
      String(tableId ?? '').trim().length > 0 &&
      isSpreadsheetTableDefinition(table)
    ))
  )) return false
  return true
}

export const isSpreadsheetSheetListResponsePayload = (value: unknown): value is SpreadsheetSheetListResponsePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (!hasValidOptionalRoutedResponseMetadata(value)) return false
  return (
    Array.isArray(value.sheets) &&
    value.sheets.every(isSpreadsheetSheetListEntry) &&
    isOptionalSpreadsheetStyleSidecars(value.styles)
  )
}

const isSpreadsheetNamedRangeEntry = (value: unknown): value is SpreadsheetNamedRangeEntry => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return typeof value.name === 'string' && value.name.trim().length > 0 && isSpreadsheetRangeAddress(value.range)
}

const isSpreadsheetDefinedNameScope = (value: unknown): value is SpreadsheetDefinedNameScope => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (value.kind === 'workbook') return true
  return value.kind === 'sheet' && typeof value.sheet_id === 'string' && value.sheet_id.trim().length > 0
}

const isSpreadsheetDefinedNameEntry = (value: unknown): value is SpreadsheetDefinedNameEntry => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    typeof value.name === 'string' &&
    value.name.trim().length > 0 &&
    isSpreadsheetDefinedNameScope(value.scope) &&
    typeof value.refers_to === 'string' &&
    value.refers_to.trim().length > 0 &&
    (value.range == null || isSpreadsheetRangeAddress(value.range)) &&
    (value.comment == null || typeof value.comment === 'string') &&
    (value.hidden == null || typeof value.hidden === 'boolean') &&
    (value.error == null || typeof value.error === 'string')
  )
}

export const isSpreadsheetNamedRangesResponsePayload = (value: unknown): value is SpreadsheetNamedRangesResponsePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (!hasValidOptionalRoutedResponseMetadata(value)) return false
  return (
    Array.isArray(value.named_ranges) &&
    value.named_ranges.every(isSpreadsheetNamedRangeEntry) &&
    Array.isArray(value.sheets) &&
    value.sheets.every(isSpreadsheetSheetListEntry)
  )
}

export const isSpreadsheetDefinedNamesResponsePayload = (value: unknown): value is SpreadsheetDefinedNamesResponsePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (!hasValidOptionalRoutedResponseMetadata(value)) return false
  return (
    Array.isArray(value.defined_names) &&
    value.defined_names.every(isSpreadsheetDefinedNameEntry) &&
    Array.isArray(value.sheets) &&
    value.sheets.every(isSpreadsheetSheetListEntry)
  )
}

export const isSpreadsheetEvaluateDraftFormulaResponsePayload = (value: unknown): value is SpreadsheetEvaluateDraftFormulaResponsePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (!hasValidOptionalRoutedResponseMetadata(value)) return false
  return isSpreadsheetCellValue(value.value) && typeof value.display_value === 'string'
}

export const isSpreadsheetValidationRulesResponsePayload = (value: unknown): value is SpreadsheetValidationRulesResponsePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (!hasValidOptionalRoutedResponseMetadata(value)) return false
  return Array.isArray(value.validation_rules) && value.validation_rules.every(isSpreadsheetValidationRule)
}

export const isSpreadsheetConditionalFormatRule = (value: unknown): value is SpreadsheetConditionalFormatRule => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    typeof value.id === 'string' &&
    value.id.trim().length > 0 &&
    (value.priority == null || Number.isFinite(Number(value.priority))) &&
    Array.isArray(value.ranges) &&
    value.ranges.every(isSpreadsheetRangeAddress) &&
    (value.stop_if_true == null || typeof value.stop_if_true === 'boolean') &&
    (value.kind == null || typeof value.kind === 'string') &&
    (value.operator == null || typeof value.operator === 'string') &&
    (value.formula1 == null || typeof value.formula1 === 'string') &&
    (value.formula2 == null || typeof value.formula2 === 'string') &&
    (value.style == null || isSpreadsheetProtocolRecord(value.style)) &&
    (value.format == null || isSpreadsheetProtocolRecord(value.format))
  )
}

export const isSpreadsheetConditionalFormatRulesResponsePayload = (
  value: unknown,
): value is SpreadsheetConditionalFormatRulesResponsePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (!hasValidOptionalRoutedResponseMetadata(value)) return false
  return (
    Array.isArray(value.conditional_format_rules) &&
    value.conditional_format_rules.every(isSpreadsheetConditionalFormatRule)
  )
}

const isSpreadsheetSparklineType = (value: unknown): value is SpreadsheetSparklineType => (
  value === 'line' || value === 'column' || value === 'winLoss'
)

const isSpreadsheetSparklinePlotBy = (value: unknown): value is SpreadsheetSparklinePlotBy => (
  value === 'rows' || value === 'columns'
)

const isSpreadsheetSparklineEmptyCellPolicy = (value: unknown): value is SpreadsheetSparklineEmptyCellPolicy => (
  value === 'zero' || value === 'gap' || value === 'span'
)

const isSpreadsheetSparklineAxisMode = (value: unknown): value is SpreadsheetSparklineAxisMode => (
  value === 'individual' || value === 'group' || value === 'custom'
)

const isSpreadsheetSparklineColor = (value: unknown): value is SpreadsheetSparklineColor | string => {
  if (typeof value === 'string') return value.trim().length > 0
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    (value.rgb == null || typeof value.rgb === 'string') &&
    (value.theme == null || isNonNegativeIntegerNumber(value.theme)) &&
    (value.tint == null || Number.isFinite(Number(value.tint))) &&
    (value.indexed == null || isNonNegativeIntegerNumber(value.indexed))
  )
}

const isSpreadsheetSparklineColorSet = (value: unknown): value is SpreadsheetSparklineColorSet => {
  if (value == null) return true
  if (!isSpreadsheetProtocolRecord(value)) return false
  return ['series', 'negative', 'axis', 'markers', 'first', 'last', 'high', 'low']
    .every((key) => value[key] == null || isSpreadsheetSparklineColor(value[key]))
}

export const isSpreadsheetSparklineEntry = (value: unknown): value is SpreadsheetSparklineEntry => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (!isSpreadsheetRangeAddress(value.location)) return false
  if (value.location.start_row !== value.location.end_row || value.location.start_col !== value.location.end_col) return false
  if (!isNullableStringField(value.source_formula)) return false
  if (value.source_range != null && !isSpreadsheetRangeAddress(value.source_range)) return false
  if (!isNullableStringField(value.date_range_formula)) return false
  if (value.date_range != null && !isSpreadsheetRangeAddress(value.date_range)) return false
  if (value.source_range_invalid != null && typeof value.source_range_invalid !== 'boolean') return false
  if (value.date_range_invalid != null && typeof value.date_range_invalid !== 'boolean') return false
  if (!isNullableStringField(value.source_range_diagnostic)) return false
  if (!isNullableStringField(value.date_range_diagnostic)) return false
  return Boolean(value.source_range || String(value.source_formula ?? '').trim())
}

export const isSpreadsheetSparklineGroup = (value: unknown): value is SpreadsheetSparklineGroup => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    typeof value.id === 'string' &&
    value.id.trim().length > 0 &&
    typeof value.sheet_id === 'string' &&
    value.sheet_id.trim().length > 0 &&
    isSpreadsheetSparklineType(value.type) &&
    isSpreadsheetSparklinePlotBy(value.plot_by) &&
    Array.isArray(value.sparklines) &&
    value.sparklines.length > 0 &&
    value.sparklines.every(isSpreadsheetSparklineEntry) &&
    (value.date_axis == null || typeof value.date_axis === 'boolean') &&
    (value.display_empty_cells_as == null || isSpreadsheetSparklineEmptyCellPolicy(value.display_empty_cells_as)) &&
    (value.display_hidden == null || typeof value.display_hidden === 'boolean') &&
    (value.display_x_axis == null || typeof value.display_x_axis === 'boolean') &&
    (value.right_to_left == null || typeof value.right_to_left === 'boolean') &&
    (value.min_axis == null || isSpreadsheetSparklineAxisMode(value.min_axis)) &&
    (value.max_axis == null || isSpreadsheetSparklineAxisMode(value.max_axis)) &&
    (value.manual_min == null || Number.isFinite(Number(value.manual_min))) &&
    (value.manual_max == null || Number.isFinite(Number(value.manual_max))) &&
    (value.line_weight == null || Number.isFinite(Number(value.line_weight))) &&
    (value.markers == null || typeof value.markers === 'boolean') &&
    (value.high_point == null || typeof value.high_point === 'boolean') &&
    (value.low_point == null || typeof value.low_point === 'boolean') &&
    (value.first_point == null || typeof value.first_point === 'boolean') &&
    (value.last_point == null || typeof value.last_point === 'boolean') &&
    (value.negative_points == null || typeof value.negative_points === 'boolean') &&
    isSpreadsheetSparklineColorSet(value.colors)
  )
}

export const isSpreadsheetSparklineGroupsResponsePayload = (
  value: unknown,
): value is SpreadsheetSparklineGroupsResponsePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (!hasValidOptionalRoutedResponseMetadata(value)) return false
  return (
    Array.isArray(value.sparkline_groups) &&
    value.sparkline_groups.every(isSpreadsheetSparklineGroup)
  )
}

const isSpreadsheetDerivedArtifactKind = (value: unknown): value is SpreadsheetDerivedArtifactKind => (
  value === 'chart' || value === 'pivot'
)

const isSpreadsheetChartArtifactType = (value: unknown): value is SpreadsheetChartArtifactType => (
  value === 'column' ||
  value === 'bar' ||
  value === 'line' ||
  value === 'area' ||
  value === 'pie' ||
  value === 'scatter'
)

export const isSpreadsheetDerivedArtifact = (value: unknown): value is SpreadsheetDerivedArtifact => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    typeof value.id === 'string' &&
    value.id.trim().length > 0 &&
    typeof value.sheet_id === 'string' &&
    value.sheet_id.trim().length > 0 &&
    isSpreadsheetDerivedArtifactKind(value.kind) &&
    isSpreadsheetRangeAddress(value.source_range) &&
    isSpreadsheetRangeAddress(value.placement) &&
    isNullableStringField(value.title) &&
    (value.chart_type == null || isSpreadsheetChartArtifactType(value.chart_type)) &&
    (value.metadata == null || isSpreadsheetProtocolRecord(value.metadata)) &&
    (value.pivot == null || isSpreadsheetProtocolRecord(value.pivot))
  )
}

export const isSpreadsheetDerivedArtifactsResponsePayload = (
  value: unknown,
): value is SpreadsheetDerivedArtifactsResponsePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (!hasValidOptionalRoutedResponseMetadata(value)) return false
  return (
    Array.isArray(value.derived_artifacts) &&
    value.derived_artifacts.every(isSpreadsheetDerivedArtifact)
  )
}

export const isSpreadsheetValidationDropdownOptionsPayload = (value: unknown): value is SpreadsheetValidationDropdownOptionsPayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return isSpreadsheetCellAddress(value.address)
}

export const isSpreadsheetValidationDropdownOptionsResponsePayload = (
  value: unknown,
): value is SpreadsheetValidationDropdownOptionsResponsePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (!hasValidOptionalRoutedResponseMetadata(value)) return false
  return (
    String(value.workbook_id ?? '').trim().length > 0 &&
    isNonNegativeIntegerNumber(value.workbook_revision) &&
    String(value.sheet_id ?? '').trim().length > 0 &&
    isSpreadsheetCellAddress(value.address) &&
    isNullableStringField(value.validation_id) &&
    (value.rule == null || isSpreadsheetValidationRule(value.rule)) &&
    isNullableStringField(value.source_formula) &&
    (value.source_ranges == null ||
      (Array.isArray(value.source_ranges) && value.source_ranges.every(isSpreadsheetRangeAddress))) &&
    Array.isArray(value.options) &&
    value.options.every((option) => typeof option === 'string') &&
    isNullableStringField(value.error)
  )
}

export const isSpreadsheetValidationIssuesPayload = (value: unknown): value is SpreadsheetValidationIssuesPayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return value.range == null || isSpreadsheetRangeAddress(value.range)
}

export const isSpreadsheetValidationIssue = (value: unknown): value is SpreadsheetValidationIssue => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  return (
    isSpreadsheetCellAddress(value.address) &&
    typeof value.validation_id === 'string' &&
    value.validation_id.trim().length > 0 &&
    (value.rule == null || isSpreadsheetValidationRule(value.rule)) &&
    typeof value.raw_input === 'string' &&
    (value.error_style == null || isSpreadsheetValidationErrorStyle(value.error_style)) &&
    isNullableStringField(value.error_title) &&
    typeof value.message === 'string'
  )
}

export const isSpreadsheetValidationIssuesResponsePayload = (
  value: unknown,
): value is SpreadsheetValidationIssuesResponsePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (!hasValidOptionalRoutedResponseMetadata(value)) return false
  return (
    String(value.workbook_id ?? '').trim().length > 0 &&
    isNonNegativeIntegerNumber(value.workbook_revision) &&
    String(value.sheet_id ?? '').trim().length > 0 &&
    (value.range == null || isSpreadsheetRangeAddress(value.range)) &&
    Array.isArray(value.issues) &&
    value.issues.every(isSpreadsheetValidationIssue)
  )
}

export const isSpreadsheetDeltaResponsePayload = (value: unknown): value is SpreadsheetDeltaResponsePayload => {
  if (!isSpreadsheetProtocolRecord(value)) return false
  if (!hasValidOptionalRoutedResponseMetadata(value)) return false
  if (!isSpreadsheetEngineDelta(value.delta)) return false
  if (value.recalc_step != null && !isSpreadsheetRecalcStepMetadata(value.recalc_step)) return false
  return value.accepted_operations == null || (
    Array.isArray(value.accepted_operations) &&
    value.accepted_operations.every(isSpreadsheetEngineOperationEnvelope)
  )
}

export const buildSpreadsheetEngineSheetId = (uiSheetId: unknown): string => {
  const text = String(uiSheetId ?? '').trim()
  if (/^sheet_[1-9]\d*$/.test(text)) return text
  const numeric = Number(text)
  if (Number.isFinite(numeric) && numeric > 0) {
    return `sheet_${Math.floor(numeric)}`
  }
  return 'sheet_1'
}

export const normalizeSpreadsheetRange = (range: SpreadsheetRangeAddress): SpreadsheetRangeAddress => {
  const startRow = normalizeSeq(range?.start_row)
  const startCol = normalizeSeq(range?.start_col)
  const endRow = normalizeSeq(range?.end_row)
  const endCol = normalizeSeq(range?.end_col)
  return {
    sheet_id: String(range?.sheet_id || '').trim(),
    start_row: Math.min(startRow, endRow),
    start_col: Math.min(startCol, endCol),
    end_row: Math.max(startRow, endRow),
    end_col: Math.max(startCol, endCol),
  }
}

export const buildSpreadsheetOperationEnvelope = <TPayload>(input: {
  op_id: string
  op_type: SpreadsheetEngineOperationType
  workbook_id: string
  sheet_id?: string | null
  base_revision: number
  actor_client_id: string
  created_at_ms: number
  payload: TPayload
}): SpreadsheetEngineOperationEnvelope<TPayload> => ({
  op_id: String(input.op_id || '').trim(),
  op_type: input.op_type,
  workbook_id: String(input.workbook_id || '').trim(),
  sheet_id: input.sheet_id == null ? null : String(input.sheet_id || '').trim(),
  base_revision: normalizeSeq(input.base_revision),
  actor_client_id: String(input.actor_client_id || '').trim(),
  created_at_ms: normalizeSeq(input.created_at_ms),
  payload: input.payload,
})

export const getSheetChunkIdsForRange = (
  range: SpreadsheetRangeAddress,
  rowBlockSize = SPREADSHEET_ENGINE_SHEET_CHUNK_ROWS,
  colBlockSize = SPREADSHEET_ENGINE_SHEET_CHUNK_COLS,
): string[] => {
  const normalized = normalizeSpreadsheetRange(range)
  const start = buildSheetChunkKey(normalized.sheet_id, normalized.start_row, normalized.start_col, rowBlockSize, colBlockSize)
  const end = buildSheetChunkKey(normalized.sheet_id, normalized.end_row, normalized.end_col, rowBlockSize, colBlockSize)
  const ids: string[] = []
  for (let rowBlock = start.row_block; rowBlock <= end.row_block; rowBlock += 1) {
    for (let colBlock = start.col_block; colBlock <= end.col_block; colBlock += 1) {
      ids.push(buildSheetChunkId({ sheet_id: normalized.sheet_id, row_block: rowBlock, col_block: colBlock }))
    }
  }
  return ids
}

const spreadsheetRangeFromOperation = (
  operation: SpreadsheetEngineOperationEnvelope,
): SpreadsheetRangeAddress | null => {
  const payload = isSpreadsheetProtocolRecord(operation?.payload) ? operation.payload : {}
  const addressPayload = isSpreadsheetProtocolRecord(payload.address) ? payload.address : null
  const rangePayload = isSpreadsheetProtocolRecord(payload.range) ? payload.range : null
  const sheetId = String(operation?.sheet_id || addressPayload?.sheet_id || rangePayload?.sheet_id || '').trim()
  if (!sheetId) return null
  if (
    operation.op_type === 'SetCellInput' ||
    operation.op_type === 'SetComment' ||
    operation.op_type === 'SetHyperlink'
  ) {
    if (!addressPayload) return null
    const row = normalizeSeq(addressPayload.row)
    const col = normalizeSeq(addressPayload.col)
    return { sheet_id: sheetId, start_row: row, start_col: col, end_row: row, end_col: col }
  }
  if (
    operation.op_type === 'SetRangeInput' ||
    operation.op_type === 'ClearRange' ||
    operation.op_type === 'SetFormat' ||
    operation.op_type === 'ClearFormats' ||
    operation.op_type === 'MergeCells' ||
    operation.op_type === 'MergeAcross' ||
    operation.op_type === 'UnmergeCells' ||
    operation.op_type === 'ClearConditionalFormats' ||
    operation.op_type === 'SetValidation' ||
    operation.op_type === 'SetProtection' ||
    operation.op_type === 'MaterializeDerivedRange'
  ) {
    const range = rangePayload
    if (!range) return null
    return normalizeSpreadsheetRange({
      sheet_id: String(range.sheet_id || sheetId).trim(),
      start_row: normalizeSeq(range.start_row),
      start_col: normalizeSeq(range.start_col),
      end_row: normalizeSeq(range.end_row),
      end_col: normalizeSeq(range.end_col),
    })
  }
  if (operation.op_type === 'SetRowFormat') {
    const rowIndex = normalizeSeq(payload.row_index)
    const count = Math.max(1, normalizeSeq(payload.count))
    return {
      sheet_id: sheetId,
      start_row: rowIndex,
      start_col: 0,
      end_row: rowIndex + count - 1,
      end_col: SPREADSHEET_ENGINE_SHEET_CHUNK_COLS - 1,
    }
  }
  if (operation.op_type === 'SetColumnFormat') {
    const colIndex = normalizeSeq(payload.col_index)
    const count = Math.max(1, normalizeSeq(payload.count))
    return {
      sheet_id: sheetId,
      start_row: 0,
      start_col: colIndex,
      end_row: SPREADSHEET_ENGINE_SHEET_CHUNK_ROWS - 1,
      end_col: colIndex + count - 1,
    }
  }
  return null
}

export const getSpreadsheetOperationDocumentSessionChunkIds = (
  operation: SpreadsheetEngineOperationEnvelope,
): string[] => {
  const range = spreadsheetRangeFromOperation(operation)
  if (!range) return []
  return getSheetChunkIdsForRange(range)
}

export const groupSpreadsheetOperationsByDocumentSessionChunk = (
  operations: SpreadsheetEngineOperationEnvelope[],
): Map<string, SpreadsheetEngineOperationEnvelope[]> => {
  const grouped = new Map<string, SpreadsheetEngineOperationEnvelope[]>()
  for (const operation of Array.isArray(operations) ? operations : []) {
    for (const chunkId of getSpreadsheetOperationDocumentSessionChunkIds(operation)) {
      const list = grouped.get(chunkId) || []
      list.push(operation)
      grouped.set(chunkId, list)
    }
  }
  return grouped
}

const cellValueFromRangeRawValue = (value: unknown, mode: SpreadsheetRangeReadMode): SpreadsheetCellValue => {
  const text = String(value ?? '')
  if (!text) return { kind: 'blank' }
  if (mode === 'computed') {
    const numberValue = Number(text)
    if (Number.isFinite(numberValue) && text.trim() !== '') {
      return { kind: 'number', value: numberValue }
    }
  }
  return { kind: 'text', value: text }
}

export const buildSpreadsheetRangeSnapshotFromGrid = (input: {
  workbook_id: string
  workbook_revision: number
  sheet_id: string
  range: SpreadsheetRangeAddress
  mode: SpreadsheetRangeReadMode
  grid: unknown[][]
}): SpreadsheetRangeSnapshot => {
  const range = normalizeSpreadsheetRange(input.range)
  const rowCount = range.end_row - range.start_row + 1
  const colCount = range.end_col - range.start_col + 1
  const rows: SpreadsheetRangeCell[][] = []
  for (let rowOffset = 0; rowOffset < rowCount; rowOffset += 1) {
    const sourceRow = Array.isArray(input.grid[rowOffset]) ? input.grid[rowOffset] : []
    const row: SpreadsheetRangeCell[] = []
    for (let colOffset = 0; colOffset < colCount; colOffset += 1) {
      const raw = sourceRow[colOffset] ?? ''
      row.push({
        row: range.start_row + rowOffset,
        col: range.start_col + colOffset,
        raw_input: String(raw ?? ''),
        value: cellValueFromRangeRawValue(raw, input.mode),
        style_id: null,
        revision: normalizeSeq(input.workbook_revision),
      })
    }
    rows.push(row)
  }
  return {
    workbook_id: String(input.workbook_id || '').trim(),
    workbook_revision: normalizeSeq(input.workbook_revision),
    sheet_id: String(input.sheet_id || '').trim(),
    range,
    mode: input.mode,
    rows,
  }
}

export const buildSpreadsheetWorkerCommand = <TPayload>(input: {
  command: SpreadsheetEngineWorkerCommandName
  requestId: string
  workbookId: string
  sheetId?: string | null
  baseRevision: number
  clientId: string
  payload: TPayload
}): SpreadsheetEngineWorkerCommand<TPayload> => ({
  protocolVersion: SPREADSHEET_ENGINE_PROTOCOL_VERSION,
  command: input.command,
  requestId: String(input.requestId || '').trim(),
  workbookId: String(input.workbookId || '').trim(),
  sheetId: input.sheetId == null ? null : String(input.sheetId || '').trim(),
  baseRevision: normalizeSeq(input.baseRevision),
  clientId: String(input.clientId || '').trim(),
  payload: input.payload,
})

export const buildSpreadsheetDocumentSessionUpdate = (
  input: {
    doc_scope: DocumentSessionDocScope
    chunk_id?: string | null
    engine_ops: SpreadsheetEngineOperationEnvelope[]
  },
): SpreadsheetDocumentSessionUpdate => ({
  document_protocol_version: DOCUMENT_SESSION_PROTOCOL_VERSION,
  doc_scope: input.doc_scope,
  chunk_id: input.chunk_id == null ? null : String(input.chunk_id || '').trim(),
  engine_ops: Array.isArray(input.engine_ops) ? input.engine_ops : [],
})

const exampleSetCellOperation = buildSpreadsheetOperationEnvelope({
  op_id: 'op_example_set_cell_a1',
  op_type: 'SetCellInput',
  workbook_id: 'workbook_example',
  sheet_id: 'sheet_example',
  base_revision: 0,
  actor_client_id: 'client_example',
  created_at_ms: 0,
  payload: {
    address: { sheet_id: 'sheet_example', row: 0, col: 0 },
    raw_input: '=SUM(B1:B4)',
  },
})

const exampleFormulaAst = {
  kind: 'function_call',
  name: 'SUM',
  args: [{
    kind: 'range_reference',
    reference: {
      start: { sheet_name: null, row: 0, col: 1, row_absolute: false, col_absolute: false },
      end: { sheet_name: null, row: 3, col: 1, row_absolute: false, col_absolute: false },
    },
  }],
} satisfies SpreadsheetFormulaAst

const exampleRangeSnapshot = {
  workbook_id: 'workbook_example',
  workbook_revision: 1,
  sheet_id: 'sheet_example',
  range: { sheet_id: 'sheet_example', start_row: 0, start_col: 0, end_row: 0, end_col: 1 },
  mode: 'computed',
  rows: [[
    {
      row: 0,
      col: 0,
      raw_input: '7',
      value: { kind: 'number', value: 7 },
      style_id: null,
      revision: 1,
    },
    {
      row: 0,
      col: 1,
      raw_input: '=SUM(B1:B4)',
      value: { kind: 'blank' },
      style_id: null,
      revision: 1,
    },
  ]],
} satisfies SpreadsheetRangeSnapshot

export const SPREADSHEET_ENGINE_PROTOCOL_EXAMPLES = {
  setCellOperation: exampleSetCellOperation,
  formulaAst: exampleFormulaAst,
  rangeSnapshot: exampleRangeSnapshot,
  applyOpsCommand: buildSpreadsheetWorkerCommand({
    command: 'sheet.applyOps',
    requestId: 'request_example_apply_ops',
    workbookId: 'workbook_example',
    sheetId: 'sheet_example',
    baseRevision: 0,
    clientId: 'client_example',
    payload: { ops: [exampleSetCellOperation] },
  }),
  getRangeCommand: buildSpreadsheetWorkerCommand<SpreadsheetGetRangePayload>({
    command: 'sheet.getRange',
    requestId: 'request_example_get_range',
    workbookId: 'workbook_example',
    sheetId: 'sheet_example',
    baseRevision: 1,
    clientId: 'client_example',
    payload: {
      range: { sheet_id: 'sheet_example', start_row: 0, start_col: 0, end_row: 0, end_col: 1 },
      mode: 'computed',
    },
  }),
  rangeResponse: {
    protocolVersion: SPREADSHEET_ENGINE_PROTOCOL_VERSION,
    response: 'sheet.range',
    requestId: 'request_example_get_range',
    workbookId: 'workbook_example',
    sheetId: 'sheet_example',
    workbookRevision: 1,
    payload: { range: exampleRangeSnapshot },
  } satisfies SpreadsheetEngineWorkerResponse<SpreadsheetRangeResponsePayload>,
  deltaResponse: {
    protocolVersion: SPREADSHEET_ENGINE_PROTOCOL_VERSION,
    response: 'sheet.delta',
    requestId: 'request_example_apply_ops',
    workbookId: 'workbook_example',
    sheetId: 'sheet_example',
    workbookRevision: 1,
    payload: {
      delta: buildSpreadsheetEngineDelta({
        workbook_id: 'workbook_example',
        workbook_revision: 1,
        changed_cells: [{ sheet_id: 'sheet_example', row: 0, col: 0 }],
        changed_ranges: [{ sheet_id: 'sheet_example', start_row: 0, start_col: 0, end_row: 0, end_col: 0 }],
        invalidated_chunk_ids: ['sheet_example::0::0'],
        recalc_status: 'dirty',
        ui_invalidations: ['viewport', 'formula_bar'],
      }),
    },
  } satisfies SpreadsheetEngineWorkerResponse<SpreadsheetDeltaResponsePayload>,
  documentSessionSheetChunkUpdate: buildSpreadsheetDocumentSessionUpdate({
    doc_scope: 'sheet_chunk',
    chunk_id: 'sheet_example::0::0',
    engine_ops: [exampleSetCellOperation],
  }),
} as const
